import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import eventService from '../../services/eventService';
import SearchableSelect from '../../components/SearchableSelect';
import ConfirmModal from '../../components/ConfirmModal';
import LockedAction from '../../components/PermissionLock';
import { usePermissions } from '../../context/PermissionContext';
import { isWeight, portionWord, gToKgStr, kgToG, fmtKg, fmtPortions, portions, fmtQty, fmtSold, costPerPortion } from '../../utils/eventQty';

const API_URL = process.env.REACT_APP_API_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DISPOSAL_REASONS = {
  employee_meal: 'Employee Meal',
  complimentary: 'Complimentary',
  security: 'Security',
  damaged: 'Damaged',
  other: 'Other',
};

const STATUS_BADGE = { draft: 'secondary', active: 'success', closed: 'dark' };

// ── helpers ───────────────────────────────────────────────────────────────────
function computeSold(dp, disposals) {
  const disposed = disposals.filter(d => d.productId === dp.productId).reduce((s, d) => s + (d.qty || 0), 0);
  if (dp.endQty == null) return null;
  return (dp.startQty || 0) + (dp.restockQty || 0) - disposed - dp.endQty;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Day Detail Modal ──────────────────────────────────────────────────────────
function DayDetailModal({ show, onClose, day, days, onNavigate, eventId, eventProducts, poolMembers, onLockToggle, onDayUpdate, onDayDelete, isManager }) {
  const [schedule, setSchedule] = useState({ assignments: [], availabilities: [] });
  const [schedLoading, setSchedLoading] = useState(false);
  const [assignForm, setAssignForm] = useState({ employeeId: '', shiftNumber: '' });
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [confirmRemoveSched, setConfirmRemoveSched] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // Day hours/shift config
  const [dayConfig, setDayConfig] = useState({ eventStart: '', eventEnd: '', shiftCount: 1, shift1Start: '', shift1End: '', shift2Start: '', shift2End: '', shift3Start: '', shift3End: '', shift4Start: '', shift4End: '' });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);

  // Inventory editing
  const [inventoryEdit, setInventoryEdit] = useState(false);
  const [inventoryDraft, setInventoryDraft] = useState({});
  const [inventorySaving, setInventorySaving] = useState(false);

  // Submit / unsubmit
  const [submitting, setSubmitting] = useState(false);

  // Delete day
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(false);
  const [deletingDay, setDeletingDay] = useState(false);

  // Disposals
  const [showAddDisposal, setShowAddDisposal] = useState(false);
  const [disposalForm, setDisposalForm] = useState({ productId: '', qty: '', reason: '', notes: '' });
  const disposalEp = (eventProducts || []).find(e => String(e.productId) === String(disposalForm.productId));
  const [addingDisposal, setAddingDisposal] = useState(false);
  const [removingDisposalId, setRemovingDisposalId] = useState(null);

  // Track which day ID we've already fetched fresh data for, to avoid infinite loops
  const initializedDayId = useRef(null);

  const loadSchedule = useCallback(async () => {
    if (!eventId || !day) return;
    setSchedLoading(true);
    try {
      const data = await eventService.getDaySchedule(eventId, day.id);
      setSchedule(data);
    } catch { setSchedule({ assignments: [], availabilities: [] }); }
    finally { setSchedLoading(false); }
  }, [eventId, day]);

  useEffect(() => {
    if (show && day) {
      const isNewDay = initializedDayId.current !== day.id;

      if (isNewDay) {
        initializedDayId.current = day.id;
        // Reset UI state when switching to a different day
        setShowAssignForm(false);
        setAssignForm({ employeeId: '', shiftNumber: '' });
        setAssignError('');
        setConfigDirty(false);
        setInventoryEdit(false);
        setInventoryDraft({});
        setConfirmDeleteDay(false);
        setShowAddDisposal(false);
        setDisposalForm({ productId: '', qty: '', reason: '', notes: '' });
        setDayConfig({
          eventStart:  day.eventStart  || '',
          eventEnd:    day.eventEnd    || '',
          shiftCount:  day.shiftCount  || 1,
          shift1Start: day.shift1Start || '',
          shift1End:   day.shift1End   || '',
          shift2Start: day.shift2Start || '',
          shift2End:   day.shift2End   || '',
          shift3Start: day.shift3Start || '',
          shift3End:   day.shift3End   || '',
          shift4Start: day.shift4Start || '',
          shift4End:   day.shift4End   || '',
        });
        // Fetch fresh day data so startQty reflects prev day's endQty
        eventService.getDayDetail(eventId, day.id)
          .then(fresh => { if (onDayUpdate) onDayUpdate(fresh); })
          .catch(() => {});
      }

      loadSchedule();
    }
    if (!show) { initializedDayId.current = null; }
  }, [show, day, loadSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedDays = (days || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const currentIdx = day ? sortedDays.findIndex(d => d.id === day.id) : -1;
  const prevDay = currentIdx > 0 ? sortedDays[currentIdx - 1] : null;
  const nextDay = currentIdx >= 0 && currentIdx < sortedDays.length - 1 ? sortedDays[currentIdx + 1] : null;

  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' && prevDay && onNavigate) onNavigate(prevDay);
      if (e.key === 'ArrowRight' && nextDay && onNavigate) onNavigate(nextDay);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [show, prevDay, nextDay, onNavigate]);

  if (!show || !day) return null;

  const updateDayConfig = (updater) => { setDayConfig(updater); setConfigDirty(true); };

  const calcHours = (start, end) => {
    if (!start || !end) return null;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60; // overnight
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const assignedIds = new Set(schedule.assignments.map(a => a.employeeId));
  const availablePool = (poolMembers || []).filter(m => !assignedIds.has(m.employeeId));
  const availByEmp = {};
  (schedule.availabilities || []).forEach(a => { availByEmp[a.employeeId] = a; });

  const AVAIL_COLOR = { available: 'success', if_needed: 'warning', unavailable: 'danger' };
  const AVAIL_LABEL = { available: 'Available', if_needed: 'If Needed', unavailable: 'Unavailable' };

  const handleAssign = async () => {
    if (!assignForm.employeeId) { setAssignError('Select an employee.'); return; }
    setAssigning(true); setAssignError('');
    try {
      await eventService.assignToDay(eventId, day.id, {
        employeeId: parseInt(assignForm.employeeId),
        shiftNumber: assignForm.shiftNumber ? parseInt(assignForm.shiftNumber) : null,
      });
      await loadSchedule();
      setShowAssignForm(false);
      setAssignForm({ employeeId: '', shiftNumber: '' });
    } catch (err) {
      setAssignError(err?.response?.data?.error || 'Failed to assign.');
    } finally { setAssigning(false); }
  };

  const handleRemoveSched = async (sched) => {
    try { await eventService.removeAssignment(eventId, day.id, sched.id); await loadSchedule(); }
    catch { /* ignore */ }
    setConfirmRemoveSched(null);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try { await eventService.publishSchedule(eventId, day.id); await loadSchedule(); }
    catch { /* ignore */ }
    finally { setPublishing(false); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const count = parseInt(dayConfig.shiftCount) || 1;
      const updated = await eventService.updateDayConfig(eventId, day.id, {
        eventStart:  dayConfig.eventStart  || null,
        eventEnd:    dayConfig.eventEnd    || null,
        shiftCount:  count,
        shift1Start: dayConfig.shift1Start || null,
        shift1End:   dayConfig.shift1End   || null,
        shift2Start: count >= 2 ? dayConfig.shift2Start || null : null,
        shift2End:   count >= 2 ? dayConfig.shift2End   || null : null,
        shift3Start: count >= 3 ? dayConfig.shift3Start || null : null,
        shift3End:   count >= 3 ? dayConfig.shift3End   || null : null,
        shift4Start: count >= 4 ? dayConfig.shift4Start || null : null,
        shift4End:   count >= 4 ? dayConfig.shift4End   || null : null,
      });
      if (onDayUpdate) onDayUpdate(updated);
      setConfigDirty(false);
    } catch { /* ignore */ }
    finally { setSavingConfig(false); }
  };

  const allPublished = schedule.assignments.length > 0 && schedule.assignments.every(a => a.published);

  const openInventoryEdit = () => {
    const draft = {};
    (eventProducts || []).forEach(ep => {
      const dp = (day.DayProducts || []).find(x => x.productId === ep.productId);
      // Sold-by-weight products are stored as grams but typed and shown in kg
      const q = (v) => (isWeight(ep) ? gToKgStr(v) : v);
      draft[ep.productId] = {
        startQty:   q(dp?.startQty   ?? ep.startingInventory ?? 0),
        restockQty: q(dp?.restockQty ?? 0),
        endQty:     dp?.endQty     != null ? q(dp.endQty) : '',
        posQty:     dp?.posQty     != null ? dp.posQty : '',
      };
    });
    setInventoryDraft(draft);
    setInventoryEdit(true);
  };

  const handleInventorySave = async () => {
    setInventorySaving(true);
    try {
      const products = Object.entries(inventoryDraft).map(([productId, vals]) => {
        const ep = (eventProducts || []).find(e => e.productId === parseInt(productId));
        const n = (v) => (v === '' ? null : (isWeight(ep) ? kgToG(v) : parseInt(v)));
        return {
          productId: parseInt(productId),
          startQty:   n(vals.startQty),
          restockQty: vals.restockQty !== '' ? n(vals.restockQty) : 0,
          endQty:     n(vals.endQty),
          posQty:     vals.posQty     !== '' ? parseInt(vals.posQty)     : null,
        };
      });
      const updated = await eventService.updateDayInventory(eventId, day.id, products);
      if (onDayUpdate) onDayUpdate(updated);
      setInventoryEdit(false);
    } catch { /* ignore */ }
    finally { setInventorySaving(false); }
  };

  const handleDeleteDay = async () => {
    setDeletingDay(true);
    try {
      await eventService.deleteDay(eventId, day.id);
      if (onDayDelete) onDayDelete(day.id);
      onClose();
    } catch { /* ignore */ }
    finally { setDeletingDay(false); setConfirmDeleteDay(false); }
  };

  const handleSubmitDay = async () => {
    setSubmitting(true);
    try {
      const updated = await eventService.submitDay(eventId, day.id);
      if (onDayUpdate) onDayUpdate(updated);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  const handleUnsubmitDay = async () => {
    setSubmitting(true);
    try {
      const updated = await eventService.unsubmitDay(eventId, day.id);
      if (onDayUpdate) onDayUpdate(updated);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  const handleAddDisposal = async () => {
    if (!disposalForm.productId || !disposalForm.qty || !disposalForm.reason) return;
    setAddingDisposal(true);
    try {
      const updated = await eventService.addDisposal(eventId, day.id, {
        productId: parseInt(disposalForm.productId),
        qty: isWeight(disposalEp) ? kgToG(disposalForm.qty) : parseInt(disposalForm.qty),
        reason: disposalForm.reason,
        notes: disposalForm.notes.trim() || null,
      });
      if (onDayUpdate) onDayUpdate(updated);
      setShowAddDisposal(false);
      setDisposalForm({ productId: '', qty: '', reason: '', notes: '' });
    } catch { /* ignore */ }
    finally { setAddingDisposal(false); }
  };

  const handleRemoveDisposal = async (disposalId) => {
    setRemovingDisposalId(disposalId);
    try {
      const updated = await eventService.removeDisposal(eventId, day.id, disposalId);
      if (onDayUpdate) onDayUpdate(updated);
    } catch { /* ignore */ }
    finally { setRemovingDisposalId(null); }
  };

  const disposalsByProduct = {};
  (day.Disposals || []).forEach(d => {
    if (!disposalsByProduct[d.productId]) disposalsByProduct[d.productId] = [];
    disposalsByProduct[d.productId].push(d);
  });

  return (
    <>
      {configDirty && (
        <style>{`@keyframes pulse-save{0%,100%{box-shadow:0 0 0 0 rgba(255,193,7,.7)}50%{box-shadow:0 0 0 8px rgba(255,193,7,0)}}.btn-save-pulse{animation:pulse-save 1.5s ease-in-out infinite!important}`}</style>
      )}
      {prevDay && onNavigate && (
        <button
          onClick={() => onNavigate(prevDay)}
          title={`← ${fmtDate(prevDay.date)}`}
          style={{ position: 'fixed', left: 'max(8px, calc(50% - 460px))', top: '50%', transform: 'translateY(-50%)', zIndex: 1075, background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(0,0,0,0.11)', borderRadius: 14, padding: '14px 12px', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 56 }}
        >
          <span style={{ fontSize: 26, lineHeight: 1, color: '#495057' }}>‹</span>
          <span style={{ fontSize: 9, color: '#6c757d', textAlign: 'center', maxWidth: 52, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{fmtDate(prevDay.date)}</span>
          {prevDay.eventStart && <span style={{ fontSize: 9, color: '#0d6efd' }}>{prevDay.eventStart.slice(0,5)}</span>}
        </button>
      )}
      {nextDay && onNavigate && (
        <button
          onClick={() => onNavigate(nextDay)}
          title={`${fmtDate(nextDay.date)} →`}
          style={{ position: 'fixed', right: 'max(8px, calc(50% - 460px))', top: '50%', transform: 'translateY(-50%)', zIndex: 1075, background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(0,0,0,0.11)', borderRadius: 14, padding: '14px 12px', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 56 }}
        >
          <span style={{ fontSize: 26, lineHeight: 1, color: '#495057' }}>›</span>
          <span style={{ fontSize: 9, color: '#6c757d', textAlign: 'center', maxWidth: 52, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{fmtDate(nextDay.date)}</span>
          {nextDay.eventStart && <span style={{ fontSize: 9, color: '#0d6efd' }}>{nextDay.eventStart.slice(0,5)}</span>}
        </button>
      )}
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{fmtDate(day.date)}</h5>
              <div className="d-flex align-items-center gap-2 ms-auto me-3">
                {day.submittedAt && <span className="badge bg-success" style={{ fontSize: 11 }}>Submitted</span>}
                {day.isLocked && <span className="badge bg-dark" style={{ fontSize: 11 }}>Locked</span>}
              </div>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>

              {/* Day Hours & Shift Config */}
              <div className="card mb-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>Hours & Shifts</h6>
                    <button
                      className={`btn btn-sm${configDirty ? ' btn-warning fw-semibold btn-save-pulse' : ' btn-outline-primary'}`}
                      style={{ fontSize: 11 }}
                      onClick={handleSaveConfig}
                      disabled={savingConfig}
                    >
                      {savingConfig ? 'Saving…' : configDirty ? '⚠ Save Changes' : 'Save'}
                    </button>
                  </div>
                  <div className="row g-2 mb-2 align-items-end">
                    <div className="col-auto">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>Event Start</label>
                      <input type="time" className="form-control form-control-sm" value={dayConfig.eventStart} onChange={e => updateDayConfig(c => ({ ...c, eventStart: e.target.value }))} />
                    </div>
                    <div className="col-auto">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>Event End</label>
                      <input type="time" className="form-control form-control-sm" value={dayConfig.eventEnd} onChange={e => updateDayConfig(c => ({ ...c, eventEnd: e.target.value }))} />
                    </div>
                    {calcHours(dayConfig.eventStart, dayConfig.eventEnd) && (
                      <div className="col-auto pb-1">
                        <span className="badge bg-dark" style={{ fontSize: 11 }}>
                          {calcHours(dayConfig.eventStart, dayConfig.eventEnd)}
                        </span>
                      </div>
                    )}
                    <div className="col-auto">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>Shifts (1–4)</label>
                      <input
                        type="number" min={1} max={4}
                        className="form-control form-control-sm"
                        style={{ width: 60 }}
                        value={dayConfig.shiftCount}
                        onChange={e => {
                          const n = Math.min(4, Math.max(1, parseInt(e.target.value) || 1));
                          updateDayConfig(c => ({ ...c, shiftCount: n }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="row g-2">
                    {[1, 2, 3, 4].filter(n => n <= parseInt(dayConfig.shiftCount)).map(n => (
                      <React.Fragment key={n}>
                        <div className="col-auto">
                          <label className="form-label mb-1" style={{ fontSize: 11 }}>Shift {n} Start</label>
                          <input type="time" className="form-control form-control-sm" value={dayConfig[`shift${n}Start`]} onChange={e => updateDayConfig(c => ({ ...c, [`shift${n}Start`]: e.target.value }))} />
                        </div>
                        <div className="col-auto">
                          <label className="form-label mb-1" style={{ fontSize: 11 }}>Shift {n} End</label>
                          <input type="time" className="form-control form-control-sm" value={dayConfig[`shift${n}End`]} onChange={e => updateDayConfig(c => ({ ...c, [`shift${n}End`]: e.target.value }))} />
                        </div>
                        {calcHours(dayConfig[`shift${n}Start`], dayConfig[`shift${n}End`]) && (
                          <div className="col-auto d-flex align-items-end pb-1">
                            <span className="badge bg-secondary" style={{ fontSize: 11 }}>
                              {calcHours(dayConfig[`shift${n}Start`], dayConfig[`shift${n}End`])}
                            </span>
                          </div>
                        )}
                        {n < parseInt(dayConfig.shiftCount) && <div className="col-12 py-0" style={{ margin: 0 }} />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/* Inventory table */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>Inventory</h6>
                {!inventoryEdit && !day.isLocked && (eventProducts || []).length > 0 && (
                  <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }} onClick={openInventoryEdit}>Enter / Edit Counts</button>
                )}
                {inventoryEdit && (
                  <div className="d-flex gap-2">
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setInventoryEdit(false)} disabled={inventorySaving}>Cancel</button>
                    <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={handleInventorySave} disabled={inventorySaving}>{inventorySaving ? 'Saving…' : 'Save'}</button>
                  </div>
                )}
              </div>
              <div className="table-responsive mb-3">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Product</th>
                      <th className="text-end">Start</th>
                      <th className="text-end">Restock</th>
                      <th className="text-end">Disposed</th>
                      <th className="text-end">End</th>
                      <th className="text-end">Sold</th>
                      <th className="text-end">POS</th>
                      {!inventoryEdit && <th className="text-end">Variance</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryEdit
                      ? (eventProducts || []).map(ep => {
                          const allDisposals = day.Disposals || [];
                          const disposed = allDisposals.filter(d => d.productId === ep.productId).reduce((s, d) => s + (d.qty || 0), 0);
                          const draft = inventoryDraft[ep.productId] || {};
                          const w = isWeight(ep);
                          const setDraftField = (field, val) => setInventoryDraft(prev => ({ ...prev, [ep.productId]: { ...prev[ep.productId], [field]: val } }));
                          return (
                            <tr key={ep.productId}>
                              <td>{ep.Product?.name || `#${ep.productId}`}</td>
                              <td className="text-end">
                                <input type="number" className="form-control form-control-sm text-end p-1" style={{ width: 64, display: 'inline-block', fontSize: 12 }} value={draft.startQty ?? ''} onChange={e => setDraftField('startQty', e.target.value)} min="0" step={w ? '0.001' : undefined} title={w ? 'kg' : undefined} />
                              </td>
                              <td className="text-end">
                                <input type="number" className="form-control form-control-sm text-end p-1" style={{ width: 64, display: 'inline-block', fontSize: 12 }} value={draft.restockQty ?? ''} onChange={e => setDraftField('restockQty', e.target.value)} min="0" step={w ? '0.001' : undefined} title={w ? 'kg' : undefined} />
                              </td>
                              <td className="text-end">{fmtQty(ep, disposed || 0)}</td>
                              <td className="text-end">
                                <input type="number" className="form-control form-control-sm text-end p-1" style={{ width: 64, display: 'inline-block', fontSize: 12 }} value={draft.endQty ?? ''} onChange={e => setDraftField('endQty', e.target.value)} min="0" step={w ? '0.001' : undefined} title={w ? 'kg' : undefined} />
                              </td>
                              <td className="text-end fw-semibold text-muted">
                                {draft.endQty !== '' && draft.startQty !== '' ? (() => {
                                  const g = (v) => (w ? (kgToG(v) || 0) : (parseInt(v) || 0));
                                  return fmtSold(ep, Math.max(0, g(draft.startQty) + g(draft.restockQty) - disposed - g(draft.endQty)));
                                })() : '—'}
                              </td>
                              <td className="text-end">
                                <input type="number" className="form-control form-control-sm text-end p-1" style={{ width: 64, display: 'inline-block', fontSize: 12 }} value={draft.posQty ?? ''} onChange={e => setDraftField('posQty', e.target.value)} min="0" />
                              </td>
                            </tr>
                          );
                        })
                      : (day.DayProducts || []).map(dp => {
                          const allDisposals = day.Disposals || [];
                          const disposed = allDisposals.filter(d => d.productId === dp.productId).reduce((s, d) => s + (d.qty || 0), 0);
                          const sold = computeSold(dp, allDisposals);
                          const ep = eventProducts.find(e => e.productId === dp.productId);
                          // POS reconciliation is per unit; it doesn't apply to sold-by-weight products
                          // (a cup can mix flavors, so per-flavor cup counts aren't meaningful)
                          const variance = !isWeight(ep) && sold != null && dp.posQty != null ? sold - dp.posQty : null;
                          return (
                            <tr key={dp.id}>
                              <td>{dp.Product?.name || ep?.Product?.name || `#${dp.productId}`}</td>
                              <td className="text-end">{fmtQty(ep, dp.startQty)}</td>
                              <td className="text-end">{fmtQty(ep, dp.restockQty || 0)}</td>
                              <td className="text-end">{fmtQty(ep, disposed || 0)}</td>
                              <td className="text-end">{fmtQty(ep, dp.endQty)}</td>
                              <td className="text-end fw-semibold">{fmtSold(ep, sold)}</td>
                              <td className="text-end">{dp.posQty ?? '—'}</td>
                              <td className="text-end">
                                {variance != null ? (
                                  <span className={variance !== 0 ? 'text-danger fw-semibold' : 'text-success'}>
                                    {variance > 0 ? '+' : ''}{variance}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                    {!inventoryEdit && (day.DayProducts || []).length === 0 && (
                      <tr><td colSpan={8} className="text-muted text-center" style={{ fontSize: 12 }}>No counts entered yet — click "Enter / Edit Counts" to add.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Disposals */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>Disposals / Free Items</h6>
                {!showAddDisposal && !day.isLocked && (
                  <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowAddDisposal(true)}>+ Add</button>
                )}
              </div>
              {showAddDisposal && (
                <div className="border rounded p-2 mb-2" style={{ background: '#f8f9fa', fontSize: 12 }}>
                  <div className="row g-2 mb-2">
                    <div className="col-5">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>Product</label>
                      <SearchableSelect
                        options={(eventProducts || []).map(ep => ({ value: ep.productId, label: ep.Product?.name || `#${ep.productId}` }))}
                        value={disposalForm.productId}
                        onChange={val => setDisposalForm(f => ({ ...f, productId: val }))}
                        placeholder="Select…"
                        size="sm"
                      />
                    </div>
                    <div className="col-2">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>{isWeight(disposalEp) ? 'Qty (kg)' : 'Qty'}</label>
                      <input type="number" min={isWeight(disposalEp) ? '0.001' : '1'} step={isWeight(disposalEp) ? '0.001' : undefined} className="form-control form-control-sm" value={disposalForm.qty} onChange={e => setDisposalForm(f => ({ ...f, qty: e.target.value }))} />
                    </div>
                    <div className="col-5">
                      <label className="form-label mb-1" style={{ fontSize: 11 }}>Reason</label>
                      <select className="form-select form-select-sm" value={disposalForm.reason} onChange={e => setDisposalForm(f => ({ ...f, reason: e.target.value }))}>
                        <option value="">Select…</option>
                        {Object.entries(DISPOSAL_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <input type="text" className="form-control form-control-sm" placeholder="Notes (optional)" value={disposalForm.notes} onChange={e => setDisposalForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={handleAddDisposal} disabled={addingDisposal || !disposalForm.productId || !disposalForm.qty || !disposalForm.reason}>{addingDisposal ? 'Saving…' : 'Save'}</button>
                    <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => { setShowAddDisposal(false); setDisposalForm({ productId: '', qty: '', reason: '', notes: '' }); }}>Cancel</button>
                  </div>
                </div>
              )}
              {(day.Disposals || []).length > 0 ? (
                <div className="table-responsive mb-3">
                  <table className="table table-sm" style={{ fontSize: 12 }}>
                    <thead className="table-light">
                      <tr><th>Product</th><th>Qty</th><th>Reason</th><th>Notes</th><th></th></tr>
                    </thead>
                    <tbody>
                      {(day.Disposals || []).map(d => (
                        <tr key={d.id}>
                          <td>{d.Product?.name || eventProducts.find(ep => ep.productId === d.productId)?.Product?.name || `#${d.productId}`}</td>
                          <td>{fmtQty(eventProducts.find(ep => ep.productId === d.productId), d.qty)}</td>
                          <td>{DISPOSAL_REASONS[d.reason] || d.reason}</td>
                          <td className="text-muted">{d.notes || '—'}</td>
                          <td>
                            <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 11 }} onClick={() => handleRemoveDisposal(d.id)} disabled={removingDisposalId === d.id}>
                              {removingDisposalId === d.id ? '…' : '✕'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !showAddDisposal && <p className="text-muted mb-3" style={{ fontSize: 12 }}>No disposals recorded for this day.</p>
              )}

              {day.notes && (
                <div className="alert alert-light py-2 mb-3" style={{ fontSize: 12 }}>
                  <strong>Notes:</strong> {day.notes}
                </div>
              )}

              {/* Schedule section */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>Schedule</h6>
                <div className="d-flex gap-2">
                  {schedule.assignments.length > 0 && !allPublished && (
                    <button className="btn btn-success btn-sm" style={{ fontSize: 11 }} onClick={handlePublish} disabled={publishing}>
                      {publishing ? 'Publishing…' : 'Publish Schedule'}
                    </button>
                  )}
                  {allPublished && <span className="badge bg-success" style={{ fontSize: 11 }}>Published</span>}
                  {!showAssignForm && (
                    <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowAssignForm(true)}>
                      + Assign
                    </button>
                  )}
                </div>
              </div>

              {schedLoading ? (
                <div className="text-center py-2"><span className="spinner-border spinner-border-sm" /></div>
              ) : (
                <>
                  {showAssignForm && (
                    <div className="card mb-2 p-2" style={{ background: '#f8f9fa' }}>
                      {assignError && <div className="alert alert-danger py-1 mb-2" style={{ fontSize: 12 }}>{assignError}</div>}
                      <div className="row g-2 align-items-end">
                        <div className="col-5">
                          <label className="form-label mb-1" style={{ fontSize: 11 }}>Employee</label>
                          <SearchableSelect
                            options={availablePool.map(m => ({ value: m.employeeId, label: `${m.Employee?.firstName || ''} ${m.Employee?.lastName || ''}`.trim() || `#${m.employeeId}` }))}
                            value={assignForm.employeeId}
                            onChange={val => setAssignForm(f => ({ ...f, employeeId: val }))}
                            placeholder="Select…"
                            size="sm"
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label mb-1" style={{ fontSize: 11 }}>Shift</label>
                          <div className="d-flex gap-1 flex-wrap">
                            {Array.from({ length: day.shiftCount || 1 }, (_, i) => i + 1).map(n => {
                              const start = day[`shift${n}Start`];
                              const end   = day[`shift${n}End`];
                              const empAvail = assignForm.employeeId ? availByEmp[parseInt(assignForm.employeeId)] : null;
                              const empShifts = empAvail?.availShifts ? empAvail.availShifts.split(',').map(Number) : null;
                              const availForThis = !empAvail ? null
                                : empAvail.status === 'unavailable' ? false
                                : !empShifts ? true
                                : empShifts.includes(n);
                              const isSelected = parseInt(assignForm.shiftNumber) === n;
                              let bg = {}, cls = 'btn-outline-secondary';
                              if (isSelected) {
                                cls = 'btn-primary';
                              } else if (availForThis === true) {
                                bg = { background: '#f0fff4', borderColor: '#a3cfbb', color: '#0f5132' };
                              } else if (availForThis === false) {
                                bg = { background: '#fff5f5', borderColor: '#f5c2c7', color: '#842029' };
                              }
                              return (
                                <button
                                  key={n}
                                  type="button"
                                  className={`btn btn-sm ${cls}`}
                                  style={{ ...bg, fontSize: 11, lineHeight: 1.2, padding: '3px 7px', minWidth: 44 }}
                                  onClick={() => setAssignForm(f => ({ ...f, shiftNumber: parseInt(f.shiftNumber) === n ? '' : String(n) }))}
                                >
                                  <div className="fw-semibold">S{n}</div>
                                  {start && <div style={{ fontSize: 9, whiteSpace: 'nowrap' }}>{start.slice(0,5)}{end ? '–'+end.slice(0,5) : ''}</div>}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              className={`btn btn-sm ${!assignForm.shiftNumber ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              style={{ fontSize: 11, padding: '3px 7px' }}
                              onClick={() => setAssignForm(f => ({ ...f, shiftNumber: '' }))}
                              title="Unspecified"
                            >—</button>
                          </div>
                        </div>
                        <div className="col-1 d-flex gap-1 pb-1">
                          <button className="btn btn-primary btn-sm" onClick={handleAssign} disabled={assigning} title="Add">{assigning ? '…' : '✓'}</button>
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setShowAssignForm(false); setAssignError(''); }} title="Cancel">✕</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {schedule.assignments.length === 0 ? (
                    <p className="text-muted small">No one assigned to this day yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm" style={{ fontSize: 12 }}>
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Shift</th>
                            <th>Availability</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedule.assignments.map(a => {
                            const avail = availByEmp[a.employeeId];
                            return (
                              <tr key={a.id}>
                                <td>{a.Employee ? `${a.Employee.firstName} ${a.Employee.lastName}` : `#${a.employeeId}`}</td>
                                <td style={{ fontSize: 12 }}>
                                  {a.shiftNumber ? (() => {
                                    const empShifts = avail?.availShifts ? avail.availShifts.split(',').map(Number) : null;
                                    const unavailable = avail?.status === 'unavailable';
                                    const mismatch = avail && (unavailable || (empShifts && !empShifts.includes(a.shiftNumber)));
                                    return (
                                      <>
                                        <span
                                          className="badge me-1"
                                          style={{ fontSize: 10, background: mismatch ? '#fd7e14' : '#0d6efd', color: '#fff' }}
                                          title={mismatch ? 'Employee not available for this shift' : undefined}
                                        >
                                          {mismatch && '⚠ '}S{a.shiftNumber}
                                        </span>
                                        {a.shiftStart && a.shiftEnd && <span className="text-muted" style={{ fontSize: 11 }}>{a.shiftStart.slice(0,5)}–{a.shiftEnd.slice(0,5)}</span>}
                                      </>
                                    );
                                  })() : <span className="text-muted">—</span>}
                                </td>
                                <td>
                                  {avail ? (
                                    <div>
                                      <span className={`badge bg-${AVAIL_COLOR[avail.status]}`} style={{ fontSize: 10 }}>
                                        {AVAIL_LABEL[avail.status]}
                                      </span>
                                      {avail.availShifts && (
                                        <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                                          Shifts: {avail.availShifts}
                                        </div>
                                      )}
                                    </div>
                                  ) : <span className="text-muted">No response</span>}
                                </td>
                                <td>
                                  {a.published
                                    ? <span className="badge bg-success" style={{ fontSize: 10 }}>{a.confirmedByEmployee ? 'Confirmed' : 'Published'}</span>
                                    : <span className="badge bg-secondary" style={{ fontSize: 10 }}>Draft</span>}
                                </td>
                                <td>
                                  <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 11 }} onClick={() => setConfirmRemoveSched(a)}>✕</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!day.submittedAt && (isManager ? (
                <LockedAction className="btn btn-outline-secondary btn-sm me-auto" what="delete a day from the event">Delete Day</LockedAction>
              ) : (
                <button
                  className="btn btn-outline-danger btn-sm me-auto"
                  onClick={() => setConfirmDeleteDay(true)}
                  disabled={deletingDay}
                  title="Delete this day from the event"
                >
                  Delete Day
                </button>
              ))}
              {day.submittedAt ? (
                <button className="btn btn-outline-warning btn-sm" onClick={handleUnsubmitDay} disabled={submitting}>
                  {submitting ? '…' : 'Un-submit'}
                </button>
              ) : (
                <button className="btn btn-success btn-sm" onClick={handleSubmitDay} disabled={submitting}>
                  {submitting ? 'Submitting…' : '✓ Submit Day'}
                </button>
              )}
              <button
                className={`btn btn-sm ${day.isLocked ? 'btn-outline-secondary' : 'btn-outline-dark'}`}
                onClick={() => onLockToggle(day)}
              >
                {day.isLocked ? 'Unlock Day' : 'Lock Day'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={!!confirmRemoveSched}
        title="Remove assignment"
        message={`Remove ${confirmRemoveSched?.Employee ? `${confirmRemoveSched.Employee.firstName} ${confirmRemoveSched.Employee.lastName}` : 'this employee'} from the schedule for this day?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => handleRemoveSched(confirmRemoveSched)}
        onCancel={() => setConfirmRemoveSched(null)}
      />

      <ConfirmModal
        show={confirmDeleteDay}
        title="Delete Day"
        message={`Delete ${fmtDate(day.date)} from this event? This will remove all inventory, schedule assignments, and availability data for this day. This cannot be undone.`}
        confirmLabel={deletingDay ? 'Deleting…' : 'Delete Day'}
        variant="danger"
        onConfirm={handleDeleteDay}
        onCancel={() => setConfirmDeleteDay(false)}
      />
    </>
  );
}

// ── Products Tab ──────────────────────────────────────────────────────────────
function ProductsTab({ event, onEventUpdate, readOnly }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEp, setEditingEp] = useState(null);
  const [form, setForm] = useState({ productId: '', startingInventory: '', salePricePerUnit: '', costPerUnit: '', forecastPct: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null); // ep to remove
  // 'catalog' = pick an existing product; 'custom' = create a product that exists only for this event
  const [mode, setMode] = useState('catalog');
  const [custom, setCustom] = useState({ name: '', sku: '', resale: false });
  const [editName, setEditName] = useState(''); // rename (event-only products only)
  // 'Sold by weight': counted in kg, sold in portions of `grams` (e.g. gelato in 140 g cups)
  const [weight, setWeight] = useState({ on: false, grams: '140', label: 'cup' });
  const wOn = weight.on;

  useEffect(() => {
    // Read-only viewers (third-party managers) can't edit, so they don't need the catalog at all.
    if (readOnly) { setLoadingProducts(false); return; }
    axios.get(`${API_URL}/api/products`, auth()).then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoadingProducts(false));
  }, [readOnly]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => { setForm({ productId: '', startingInventory: '', salePricePerUnit: '', costPerUnit: '', forecastPct: '' }); setMode('catalog'); setCustom({ name: '', sku: '', resale: false }); setWeight({ on: false, grams: '140', label: 'cup' }); setEditingEp(null); setShowAddForm(true); setError(''); };
  const openEdit = (ep) => { setEditName(ep.Product?.eventId ? (ep.Product.name || '') : ''); setWeight({ on: isWeight(ep), grams: String(ep.portionGrams || 140), label: ep.portionLabel || 'cup' }); setForm({ productId: ep.productId, startingInventory: isWeight(ep) ? gToKgStr(ep.startingInventory) : ep.startingInventory, salePricePerUnit: ep.salePricePerUnit ?? '', costPerUnit: ep.costPerUnit ?? '', forecastPct: ep.forecastPct ?? '' }); setEditingEp(ep); setShowAddForm(true); setError(''); };

  const handleSave = async () => {
    if (wOn && !(parseInt(weight.grams) > 0)) { setError('Enter the portion size in grams (e.g. 140).'); return; }
    const isNewCustom = !editingEp && mode === 'custom';
    if (isNewCustom && !custom.name.trim()) { setError('Enter a name for the event-only product.'); return; }
    if (!isNewCustom && !form.productId) { setError('Select a product.'); return; }
    const renaming = !!editingEp?.Product?.eventId;
    if (renaming && !editName.trim()) { setError('Enter a name for the product.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...(isNewCustom
          ? { newProduct: { name: custom.name.trim(), sku: custom.sku.trim() || undefined, productType: custom.resale ? 'resale' : 'manufactured' } }
          : { productId: parseInt(form.productId) }),
        ...(renaming && { productName: editName.trim() }),
        // Sold-by-weight: stored as whole grams (typed in kg); price is per portion, cost is per kg
        startingInventory: wOn ? (kgToG(form.startingInventory) || 0) : (parseInt(form.startingInventory) || 0),
        ...((wOn || editingEp) && { portionGrams: wOn ? (parseInt(weight.grams) || null) : null, portionLabel: wOn ? (weight.label.trim() || null) : null }),
        salePricePerUnit: form.salePricePerUnit !== '' ? parseFloat(form.salePricePerUnit) : null,
        costPerUnit: form.costPerUnit !== '' ? parseFloat(form.costPerUnit) : null,
        forecastPct: form.forecastPct !== '' ? parseFloat(form.forecastPct) : null,
      };
      if (editingEp) {
        await eventService.updateProduct(event.id, editingEp.id, payload);
      } else {
        await eventService.addProduct(event.id, payload);
      }
      const updated = await eventService.getEvent(event.id);
      onEventUpdate(updated);
      setShowAddForm(false);
      setSaving(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save.');
      setSaving(false);
    }
  };

  const handleRemove = async (ep) => {
    try {
      await eventService.removeProduct(event.id, ep.id);
      const updated = await eventService.getEvent(event.id);
      onEventUpdate(updated);
    } catch { /* ignore */ }
    setConfirmRemove(null);
  };

  const eventProducts = event.EventProducts || [];
  const alreadyAdded = new Set(eventProducts.map(ep => ep.productId));
  const availableProducts = products.filter(p => !alreadyAdded.has(p.id) || editingEp?.productId === p.id);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">{eventProducts.length} product{eventProducts.length !== 1 ? 's' : ''} in this event</span>
        {readOnly
          ? <LockedAction what="add, change or remove products">Add Product</LockedAction>
          : !showAddForm && <button className="btn btn-sm btn-outline-primary" onClick={openAdd}>+ Add Product</button>}
      </div>

      {showAddForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-3" style={{ fontSize: 13 }}>{editingEp ? 'Edit Product' : 'Add Product'}</h6>
            {error && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
            {loadingProducts ? <div className="text-muted small">Loading products…</div> : (
              <>
                {!editingEp && (
                  <div className="btn-group btn-group-sm mb-2" role="group">
                    <button type="button" className={`btn ${mode === 'catalog' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('catalog')}>From catalog</button>
                    <button type="button" className={`btn ${mode === 'custom' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('custom')}>Event-only product</button>
                  </div>
                )}
                {editingEp?.Product?.eventId && (
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Name <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={editName} onChange={e => setEditName(e.target.value)} />
                  </div>
                )}
                {!editingEp && mode === 'custom' && (
                  <div className="row g-2 mb-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Name <span className="text-danger">*</span></label>
                      <input className="form-control form-control-sm" value={custom.name} onChange={e => setCustom(c => ({ ...c, name: e.target.value }))} placeholder="e.g. Bottled water" />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>SKU <span className="text-muted">(optional)</span></label>
                      <input className="form-control form-control-sm" value={custom.sku} onChange={e => setCustom(c => ({ ...c, sku: e.target.value }))} />
                    </div>
                    <div className="col-6 col-md-3 d-flex align-items-end">
                      <div className="form-check">
                        <input id="evp-resale" type="checkbox" className="form-check-input" checked={custom.resale} onChange={e => setCustom(c => ({ ...c, resale: e.target.checked }))} />
                        <label htmlFor="evp-resale" className="form-check-label" style={{ fontSize: 12 }}>Resale item</label>
                      </div>
                    </div>
                    <div className="col-12 text-muted" style={{ fontSize: 11 }}>Only exists for this event — it won't appear in the general product list.</div>
                  </div>
                )}
                {!editingEp && mode === 'catalog' && (
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Product <span className="text-danger">*</span></label>
                    <SearchableSelect
                      options={availableProducts.map(p => ({ value: p.id, label: p.name }))}
                      value={form.productId}
                      onChange={val => set('productId', val)}
                      placeholder="Select product…"
                      size="sm"
                    />
                  </div>
                )}
                <div className="mb-2">
                  <div className="form-check">
                    <input id="evp-weight" type="checkbox" className="form-check-input" checked={wOn} onChange={e => setWeight(w => ({ ...w, on: e.target.checked }))} />
                    <label htmlFor="evp-weight" className="form-check-label" style={{ fontSize: 12 }}>Sold by weight <span className="text-muted">— counted in kg, sold in portions (e.g. gelato cups)</span></label>
                  </div>
                  {wOn && (
                    <div className="row g-2 mt-1">
                      <div className="col-6 col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Portion size (g) <span className="text-danger">*</span></label>
                        <input type="number" min="1" step="1" className="form-control form-control-sm" value={weight.grams} onChange={e => setWeight(w => ({ ...w, grams: e.target.value }))} placeholder="140" />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Portion called</label>
                        <input className="form-control form-control-sm" maxLength={20} value={weight.label} onChange={e => setWeight(w => ({ ...w, label: e.target.value }))} placeholder="cup" />
                      </div>
                      <div className="col-12 text-muted" style={{ fontSize: 11 }}>You weigh in kg; the system works out portions sold and revenue. Enter the price for one {weight.label.trim() || 'portion'} and the cost for one kg.</div>
                    </div>
                  )}
                </div>
                <div className="row g-2">
                  <div className="col-6 col-lg-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Starting Inventory{wOn ? ' (kg)' : ''}</label>
                    <input type="number" min="0" step={wOn ? '0.001' : undefined} className="form-control form-control-sm" value={form.startingInventory} onChange={e => set('startingInventory', e.target.value)} placeholder="0" />
                  </div>
                  <div className="col-6 col-lg-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>{wOn ? `Price per ${weight.label.trim() || 'portion'} ($)` : 'Sale Price / unit ($)'}</label>
                    <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.salePricePerUnit} onChange={e => set('salePricePerUnit', e.target.value)} placeholder="e.g. 15.00" />
                  </div>
                  <div className="col-6 col-lg-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>{wOn ? 'Cost per kg ($)' : 'Cost / unit ($)'} <span className="text-muted" style={{ fontSize: 10 }}>incl. margin</span></label>
                    <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.costPerUnit} onChange={e => set('costPerUnit', e.target.value)} placeholder="e.g. 4.50" />
                  </div>
                  <div className="col-6 col-lg-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      Forecast Mix %
                      {(() => {
                        const otherPct = eventProducts.filter(ep => !editingEp || ep.id !== editingEp.id).reduce((s, ep) => s + (parseFloat(ep.forecastPct) || 0), 0);
                        const thisVal = parseFloat(form.forecastPct) || 0;
                        const total = Math.round((otherPct + thisVal) * 10) / 10;
                        const color = total === 100 ? '#198754' : total > 100 ? '#dc3545' : '#6c757d';
                        return <span className="ms-1 fw-semibold" style={{ fontSize: 10, color }}>{total}% total</span>;
                      })()}
                    </label>
                    <input type="number" min="0" max="100" step="1" className="form-control form-control-sm" value={form.forecastPct} onChange={e => set('forecastPct', e.target.value)} placeholder="e.g. 50" />
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddForm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {eventProducts.length === 0 ? (
        <p className="text-muted small">No products added yet. Add products to track inventory and calculate revenue.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm" style={{ fontSize: 13 }}>
            <thead className="table-light">
              <tr>
                <th>Product</th>
                <th className="text-end">Starting Qty</th>
                <th className="text-end">Sale Price</th>
                <th className="text-end">Cost/Unit</th>
                <th className="text-end">Forecast</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {eventProducts.map(ep => (
                <tr key={ep.id}>
                  <td>
                    {ep.Product?.name || `#${ep.productId}`}
                    {isWeight(ep) && <span className="badge bg-light text-dark border ms-2" style={{ fontSize: 10 }} title={`${ep.portionGrams} g per ${portionWord(ep, 1)}`}>By weight · {ep.portionGrams} g</span>}
                    {ep.Product?.eventId && <span className="badge bg-info text-dark ms-2" style={{ fontSize: 10 }}>Event-only{ep.Product.productType === 'resale' ? ' · Resale' : ''}</span>}
                  </td>
                  <td className="text-end">{fmtQty(ep, ep.startingInventory)}</td>
                  <td className="text-end">{ep.salePricePerUnit != null ? fmt$(ep.salePricePerUnit) : '—'}{isWeight(ep) && ep.salePricePerUnit != null && <span className="text-muted" style={{ fontSize: 11 }}> / {portionWord(ep, 1)}</span>}</td>
                  <td className="text-end">{ep.costPerUnit != null ? fmt$(ep.costPerUnit) : '—'}{isWeight(ep) && ep.costPerUnit != null && <span className="text-muted" style={{ fontSize: 11 }}> / kg</span>}</td>
                  <td className="text-end">{ep.forecastPct != null ? `${ep.forecastPct}%` : '—'}</td>
                  <td className="text-end">
                    {readOnly ? (
                      <LockedAction className="btn btn-link btn-sm p-0" what="add, change or remove products">Edit</LockedAction>
                    ) : (
                      <>
                        <button className="btn btn-link btn-sm p-0 me-2" onClick={() => openEdit(ep)}>Edit</button>
                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setConfirmRemove(ep)}>Remove</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(() => {
            const total = Math.round(eventProducts.reduce((s, ep) => s + (parseFloat(ep.forecastPct) || 0), 0) * 10) / 10;
            if (total === 0) return null;
            return (
              <div className={`d-flex align-items-center gap-2 mt-1 px-1`} style={{ fontSize: 12 }}>
                <span className="text-muted">Forecast total:</span>
                <span className={`fw-semibold ${total === 100 ? 'text-success' : 'text-danger'}`}>{total}%</span>
                {total !== 100 && <span className="text-danger" style={{ fontSize: 11 }}>⚠ Should add up to 100% for accurate break-even</span>}
              </div>
            );
          })()}
        </div>
      )}

      <ConfirmModal
        show={!!confirmRemove}
        title="Remove product"
        message={`Remove ${confirmRemove?.Product?.name} from this event? This cannot be undone.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => handleRemove(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}

// ── Schedule PDF Import Modal ─────────────────────────────────────────────────
function ScheduleImportModal({ show, onClose, event, onApplied }) {
  const [step, setStep] = useState('upload'); // upload | review | done
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [results, setResults] = useState([]); // [{ date, eventStart, eventEnd, label, matched }]
  const [checked, setChecked] = useState(new Set()); // dates to apply
  const [edited, setEdited] = useState({}); // { [date]: { eventStart, eventEnd } }
  const [applying, setApplying] = useState(false);
  const [applyCount, setApplyCount] = useState(null);
  const fileRef = React.useRef();

  // Reset when modal opens
  useEffect(() => {
    if (show) {
      setStep('upload'); setFile(null); setParsing(false); setParseError('');
      setResults([]); setChecked(new Set()); setEdited({}); setApplying(false); setApplyCount(null);
    }
  }, [show]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setParseError(''); }
  };

  const handleExtract = async () => {
    if (!file) return;
    setParsing(true); setParseError('');
    try {
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]); // strip data:...;base64,
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { results: extracted } = await eventService.parseSchedulePdf(event.id, pdfBase64);
      setResults(extracted);
      // Pre-check all matched days
      setChecked(new Set(extracted.filter(r => r.matched).map(r => r.date)));
      setEdited({});
      setStep('review');
    } catch (err) {
      setParseError(err?.response?.data?.detail || err?.response?.data?.error || 'Extraction failed. Try a different file.');
    } finally {
      setParsing(false);
    }
  };

  const handleApply = async () => {
    const days = results
      .filter(r => checked.has(r.date))
      .map(r => ({
        date: r.date,
        eventStart: edited[r.date]?.eventStart ?? r.eventStart,
        eventEnd:   edited[r.date]?.eventEnd   ?? r.eventEnd,
      }));
    if (!days.length) return;
    setApplying(true);
    try {
      const { updated } = await eventService.bulkUpdateDayHours(event.id, days);
      setApplyCount(updated);
      setStep('done');
      onApplied();
    } catch (err) {
      setParseError(err?.response?.data?.error || 'Failed to apply. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const setEdit = (date, field, value) =>
    setEdited(prev => ({ ...prev, [date]: { ...(prev[date] || {}), [field]: value } }));

  const toggleCheck = (date) =>
    setChecked(prev => { const n = new Set(prev); n.has(date) ? n.delete(date) : n.add(date); return n; });

  if (!show) return null;

  const checkedCount = checked.size;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1080 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1090 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">📄 Import Schedule from PDF</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">

              {/* ── Step 1: Upload ── */}
              {step === 'upload' && (
                <div>
                  <p className="text-muted" style={{ fontSize: 13 }}>
                    Upload your event schedule PDF. The AI will read the document and extract operating hours for each day, which you can review before applying.
                  </p>
                  <div
                    className="border rounded p-4 text-center"
                    style={{ borderStyle: 'dashed', background: '#f8f9fa', cursor: 'pointer' }}
                    onClick={() => fileRef.current?.click()}
                  >
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                      {file ? file.name : 'Click to select a PDF'}
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {file ? `${(file.size / 1024).toFixed(0)} KB` : 'PDF files only'}
                    </div>
                    <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />
                  </div>
                  {parseError && <div className="alert alert-danger mt-3 py-2" style={{ fontSize: 13 }}>{parseError}</div>}
                </div>
              )}

              {/* ── Step 2: Review ── */}
              {step === 'review' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{ fontSize: 13 }}>
                      <strong>{results.length}</strong> days extracted —{' '}
                      <span className="text-success fw-semibold">{results.filter(r => r.matched).length} match your event days</span>
                      {results.filter(r => !r.matched).length > 0 && (
                        <span className="text-muted ms-2">({results.filter(r => !r.matched).length} outside event range, unchecked)</span>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }}
                        onClick={() => setChecked(new Set(results.filter(r => r.matched).map(r => r.date)))}>
                        Select matched
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }}
                        onClick={() => setChecked(new Set(results.map(r => r.date)))}>
                        Select all
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }}
                        onClick={() => setChecked(new Set())}>
                        Clear
                      </button>
                    </div>
                  </div>
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <table className="table table-sm" style={{ fontSize: 12 }}>
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 32 }}></th>
                          <th>Day</th>
                          <th style={{ width: 110 }}>Start</th>
                          <th style={{ width: 110 }}>End</th>
                          <th style={{ width: 80 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map(r => {
                          const isChecked = checked.has(r.date);
                          const curStart = edited[r.date]?.eventStart ?? r.eventStart;
                          const curEnd   = edited[r.date]?.eventEnd   ?? r.eventEnd;
                          return (
                            <tr key={r.date} style={{ background: isChecked ? '#f0fff4' : undefined, opacity: isChecked ? 1 : 0.55 }}>
                              <td>
                                <input type="checkbox" checked={isChecked} onChange={() => toggleCheck(r.date)} />
                              </td>
                              <td className="fw-semibold">{r.label || r.date}</td>
                              <td>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={curStart}
                                  onChange={e => setEdit(r.date, 'eventStart', e.target.value)}
                                  disabled={!isChecked}
                                />
                              </td>
                              <td>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={curEnd}
                                  onChange={e => setEdit(r.date, 'eventEnd', e.target.value)}
                                  disabled={!isChecked}
                                />
                              </td>
                              <td>
                                {r.matched
                                  ? <span className="badge bg-success" style={{ fontSize: 10 }}>✓ In event</span>
                                  : <span className="badge bg-secondary" style={{ fontSize: 10 }}>Outside range</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {parseError && <div className="alert alert-danger mt-2 py-2" style={{ fontSize: 13 }}>{parseError}</div>}
                </div>
              )}

              {/* ── Step 3: Done ── */}
              {step === 'done' && (
                <div className="text-center py-3">
                  <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                  <div className="fw-bold" style={{ fontSize: 16 }}>Done!</div>
                  <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                    Updated event hours for <strong>{applyCount}</strong> day{applyCount !== 1 ? 's' : ''}.
                  </div>
                  <p className="text-muted mt-2" style={{ fontSize: 12 }}>
                    Open any day card to verify the hours and add shift details.
                  </p>
                </div>
              )}

            </div>
            <div className="modal-footer">
              {step === 'upload' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={handleExtract} disabled={!file || parsing}>
                    {parsing ? <><span className="spinner-border spinner-border-sm me-1" />Extracting…</> : 'Extract Schedule'}
                  </button>
                </>
              )}
              {step === 'review' && (
                <>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep('upload')}>← Back</button>
                  <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
                  <button className="btn btn-success btn-sm" onClick={handleApply} disabled={checkedCount === 0 || applying}>
                    {applying ? <><span className="spinner-border spinner-border-sm me-1" />Applying…</> : `Apply to ${checkedCount} day${checkedCount !== 1 ? 's' : ''}`}
                  </button>
                </>
              )}
              {step === 'done' && (
                <button className="btn btn-primary btn-sm" onClick={onClose}>Close</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Calendar Tab ──────────────────────────────────────────────────────────────
function CalendarTab({ event, onDayClick }) {
  const scrollRef = useRef(null);
  const [dayLaborMap, setDayLaborMap] = useState({});
  const [dayRevMap, setDayRevMap] = useState({});

  useEffect(() => {
    eventService.getDayLabor(event.id)
      .then(list => {
        const m = {};
        (list || []).forEach(d => { m[d.dayId] = d.laborCost; });
        setDayLaborMap(m);
      })
      .catch(() => {});
    eventService.getDayRevenue(event.id)
      .then(list => {
        const m = {};
        (list || []).forEach(d => { m[d.dayId] = d; });
        setDayRevMap(m);
      })
      .catch(() => {});
  // Re-fetch when any day's submitted state changes (count of submitted days as proxy)
  }, [event.id, (event.EventDays || []).filter(d => d.submittedAt).length]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = (event.EventDays || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const eventProducts = event.EventProducts || [];
  const totalDays = days.length || 1;

  // Weighted net margin per unit across all products using forecastPct
  const netMarginPerUnit = (() => {
    const products = eventProducts.filter(ep => ep.forecastPct != null && ep.salePricePerUnit != null);
    if (!products.length) return null;
    const totalPct = products.reduce((s, ep) => s + parseFloat(ep.forecastPct), 0);
    if (totalPct <= 0) return null;
    const commPct = parseFloat(event.concessionsCommissionPct) || 0;
    let margin = 0;
    for (const ep of products) {
      const w = parseFloat(ep.forecastPct) / totalPct;
      const revenue = parseFloat(ep.salePricePerUnit) * (1 - commPct / 100);
      const cost = costPerPortion(ep);
      const entranceFee = parseFloat(event.entranceCommissionPerUnit) || 0;
      margin += w * (revenue - cost - entranceFee);
    }
    return margin > 0 ? margin : null;
  })();

  const calcBreakEven = (day) => {
    if (!netMarginPerUnit) return null;
    // Use the same laborCost as net revenue (actual TimeEntry hours when available)
    // so break-even and net revenue are always consistent with each other
    const laborCost = dayRevMap[day.id]?.laborCost ?? dayLaborMap[day.id] ?? 0;
    const entryFeeShare = (parseFloat(event.entryFee) || 0) / totalDays;
    const totalCost = laborCost + entryFeeShare;
    if (totalCost <= 0) return null;
    return Math.ceil(totalCost / netMarginPerUnit);
  };

  const getDaySummary = (day) => {
    const products = day.DayProducts || [];
    const disposals = day.Disposals || [];
    let totalSold = 0;
    let totalRevenue = 0;
    let hasVariance = false;

    for (const dp of products) {
      const sold = computeSold(dp, disposals);
      if (sold != null) {
        const ep = eventProducts.find(e => e.productId === dp.productId);
        const units = portions(ep, sold); // portions for sold-by-weight products, plain units otherwise
        totalSold += units;
        if (ep?.salePricePerUnit) totalRevenue += units * parseFloat(ep.salePricePerUnit);
        if (!isWeight(ep) && dp.posQty != null && sold !== dp.posQty) hasVariance = true;
      }
    }

    const submitted = !!day.submittedAt;
    return { totalSold, totalRevenue, hasVariance, submitted, locked: day.isLocked };
  };

  return (
    <div>
      <p className="text-muted small mb-3">
        Click any day to view details. <span style={{ color: '#0d6efd' }}>Blue border</span> = hours configured · <span style={{ color: '#198754' }}>Green</span> = submitted. Use ← → keys to navigate days.
      </p>
      <EventSummary event={event} dayRevMap={dayRevMap} />
      <div ref={scrollRef} style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ display: 'flex', gap: 10, minWidth: 'max-content' }}>
          {days.map(day => {
            const { totalSold, totalRevenue, hasVariance, submitted, locked } = getDaySummary(day);
            return (
              <div key={day.id}
                onClick={() => onDayClick(day)}
                style={{
                  width: 160,
                  border: `2px solid ${submitted ? '#198754' : day.eventStart ? '#0d6efd' : '#dee2e6'}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  background: submitted ? '#f0fdf4' : day.eventStart ? '#f0f7ff' : '#fff',
                  flexShrink: 0,
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>{fmtDate(day.date)}</div>
                <hr className="my-1" style={{ borderColor: '#dee2e6' }} />
                {(day.DayProducts || []).map(dp => {
                  const ep = eventProducts.find(e => e.productId === dp.productId);
                  const sold = computeSold(dp, day.Disposals || []);
                  return (
                    <div key={dp.productId} className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                      <span className="text-truncate me-1" style={{ maxWidth: 90 }}>{dp.Product?.name || ep?.Product?.name || `#${dp.productId}`}</span>
                      <span className="fw-semibold">{sold != null && isWeight(ep) ? fmtPortions(portions(ep, sold)) : (sold ?? '—')}</span>
                    </div>
                  );
                })}
                {(day.DayProducts || []).length === 0 && (
                  <div className="text-muted" style={{ fontSize: 11 }}>No data</div>
                )}
                {totalSold > 0 && (
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: 11, borderTop: '1px solid #dee2e6', paddingTop: 3 }}>
                    <span className="text-muted">Total sold</span>
                    <span className="fw-bold">{fmtPortions(totalSold)}</span>
                  </div>
                )}
                <hr className="my-1" style={{ borderColor: '#dee2e6' }} />
                <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                  <span className="text-muted">Revenue</span>
                  <span className="fw-semibold">{totalRevenue > 0 ? fmt$(totalRevenue) : '—'}</span>
                </div>
                {dayRevMap[day.id]?.hasData && (
                  <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                    <span className="text-muted">Net</span>
                    <span className="fw-semibold" style={{ color: dayRevMap[day.id].netRevenue >= 0 ? '#198754' : '#dc3545' }}>
                      {fmt$(dayRevMap[day.id].netRevenue)}
                    </span>
                  </div>
                )}
                <div className="d-flex gap-1 mt-1 flex-wrap">
                  {submitted && <span className="badge bg-success" style={{ fontSize: 9 }}>Submitted</span>}
                  {locked && <span className="badge bg-dark" style={{ fontSize: 9 }}>Locked</span>}
                  {hasVariance && <span className="badge bg-warning text-dark" style={{ fontSize: 9 }}>Variance</span>}
                </div>
                {day.eventStart && (
                  <div style={{ fontSize: 10, color: submitted ? '#198754' : '#0d6efd', marginTop: 4 }}>
                    ⏱ {day.eventStart.slice(0,5)}{day.eventEnd ? '–'+day.eventEnd.slice(0,5) : ''}{day.shiftCount > 1 ? ` · ${day.shiftCount}S` : ''}
                  </div>
                )}
                {(() => {
                  const be = calcBreakEven(day);
                  if (!be) return null;
                  return (
                    <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed #dee2e6' }}>
                      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6c757d' }}>Break-even</div>
                      <div className="fw-semibold" style={{ fontSize: 13, color: '#6f42c1' }}>⚖ {be} units</div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Event Performance Summary ─────────────────────────────────────────────────
function EventSummary({ event, dayRevMap }) {
  const eventProducts = event.EventProducts || [];
  const submittedDays = (event.EventDays || []).filter(d => d.submittedAt);
  if (!submittedDays.length) return null;

  // Units sold per product across all submitted days
  const soldByProduct = {};
  submittedDays.forEach(day => {
    const disposals = day.Disposals || [];
    (day.DayProducts || []).forEach(dp => {
      const sold = computeSold(dp, disposals);
      if (sold == null || sold <= 0) return;
      const ep = eventProducts.find(e => e.productId === dp.productId);
      // portions for sold-by-weight products (stored as grams), so totals across products make sense
      soldByProduct[dp.productId] = (soldByProduct[dp.productId] || 0) + portions(ep, sold);
    });
  });
  const totalSold = Object.values(soldByProduct).reduce((s, v) => s + v, 0);

  const productRows = eventProducts
    .map(ep => ({ ep, sold: soldByProduct[ep.productId] || 0 }))
    .filter(r => r.sold > 0)
    .sort((a, b) => b.sold - a.sold);

  // Aggregate financials from dayRevMap (only days with actual revenue data).
  // Entry fee is a fixed upfront cost for the whole event — use event.entryFee directly
  // rather than summing per-day shares, and recompute net revenue from components.
  const totals = Object.values(dayRevMap).reduce((acc, d) => {
    if (!d.hasData) return acc;
    acc.grossRevenue          += d.grossRevenue          || 0;
    acc.productCost           += d.productCost           || 0;
    acc.laborCost             += d.laborCost             || 0;
    acc.concessionsCommission += d.concessionsCommission || 0;
    acc.entranceCommission    += d.entranceCommission    || 0;
    return acc;
  }, { grossRevenue: 0, productCost: 0, laborCost: 0, concessionsCommission: 0, entranceCommission: 0 });

  const entryFee = parseFloat(event.entryFee) || 0;
  const netRevenue = totals.grossRevenue - totals.productCost - totals.laborCost - entryFee - totals.concessionsCommission - totals.entranceCommission;
  const totalExpenses = totals.productCost + totals.laborCost + entryFee + totals.concessionsCommission + totals.entranceCommission;
  const hasFinancials = totals.grossRevenue > 0 || totalExpenses > 0;

  const pct = (v) => totalSold > 0 ? `${Math.round(v / totalSold * 100)}%` : '0%';

  return (
    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, padding: '14px 16px', marginBottom: 16, background: '#fff' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6c757d', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
        Event Performance &nbsp;·&nbsp; {submittedDays.length} submitted day{submittedDays.length !== 1 ? 's' : ''}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: productRows.length && hasFinancials ? '1fr 1fr' : '1fr', gap: 24 }}>
        {/* Units sold */}
        {productRows.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#495057', textTransform: 'uppercase', marginBottom: 8 }}>Units Sold</div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {productRows.map(({ ep, sold }) => (
                  <tr key={ep.productId}>
                    <td style={{ paddingBottom: 4, color: '#495057', paddingRight: 10 }}>{ep.Product?.name || `#${ep.productId}`}</td>
                    <td style={{ paddingBottom: 4, textAlign: 'right', fontWeight: 600, paddingRight: 8, whiteSpace: 'nowrap' }}>{Number.isInteger(sold) ? sold.toLocaleString() : sold.toFixed(1)}</td>
                    <td style={{ paddingBottom: 4, textAlign: 'right', color: '#adb5bd', fontSize: 11, whiteSpace: 'nowrap' }}>{pct(sold)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid #dee2e6' }}>
                  <td style={{ paddingTop: 6, fontWeight: 700, fontSize: 13 }}>Total</td>
                  <td style={{ paddingTop: 6, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{Number.isInteger(totalSold) ? totalSold.toLocaleString() : totalSold.toFixed(1)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Revenue & Expenses */}
        {hasFinancials && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#495057', textTransform: 'uppercase', marginBottom: 8 }}>Revenue & Expenses</div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ paddingBottom: 4 }}>Gross revenue</td>
                  <td style={{ paddingBottom: 4, textAlign: 'right', fontWeight: 600, color: '#198754' }}>{fmt$(totals.grossRevenue)}</td>
                </tr>
                {totals.productCost > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 3, color: '#6c757d', paddingLeft: 12 }}>Product cost</td>
                    <td style={{ paddingBottom: 3, textAlign: 'right', color: '#6c757d' }}>−{fmt$(totals.productCost)}</td>
                  </tr>
                )}
                {totals.laborCost > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 3, color: '#6c757d', paddingLeft: 12 }}>Labor</td>
                    <td style={{ paddingBottom: 3, textAlign: 'right', color: '#6c757d' }}>−{fmt$(totals.laborCost)}</td>
                  </tr>
                )}
                {entryFee > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 3, color: '#6c757d', paddingLeft: 12 }}>Entry fee</td>
                    <td style={{ paddingBottom: 3, textAlign: 'right', color: '#6c757d' }}>−{fmt$(entryFee)}</td>
                  </tr>
                )}
                {totals.concessionsCommission > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 3, color: '#6c757d', paddingLeft: 12 }}>Concessions commission {event.concessionsCommissionPct ? `(${event.concessionsCommissionPct}%)` : ''}</td>
                    <td style={{ paddingBottom: 3, textAlign: 'right', color: '#6c757d' }}>−{fmt$(totals.concessionsCommission)}</td>
                  </tr>
                )}
                {totals.entranceCommission > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 3, color: '#6c757d', paddingLeft: 12 }}>Entrance commission</td>
                    <td style={{ paddingBottom: 3, textAlign: 'right', color: '#6c757d' }}>−{fmt$(totals.entranceCommission)}</td>
                  </tr>
                )}
                {totalExpenses > 0 && (
                  <tr>
                    <td style={{ paddingBottom: 6, color: '#6c757d', paddingLeft: 12 }}>Total expenses</td>
                    <td style={{ paddingBottom: 6, textAlign: 'right', color: '#6c757d' }}>−{fmt$(totalExpenses)}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid #dee2e6' }}>
                  <td style={{ paddingTop: 6, fontWeight: 700 }}>Net revenue</td>
                  <td style={{ paddingTop: 6, textAlign: 'right', fontWeight: 700, fontSize: 15, color: netRevenue >= 0 ? '#198754' : '#dc3545' }}>
                    {fmt$(netRevenue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Supply Tab ────────────────────────────────────────────────────────────────
const STATUS_COLOR = { pending: 'secondary', confirmed: 'primary', in_production: 'warning', ready: 'success', shipped: 'dark', invoiced: 'dark', cancelled: 'danger' };

function SupplyTab({ event }) {
  const [orders, setOrders] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ requestedDate: '', notes: '', items: [{ productId: '', quantity: '' }] });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Only show products that are already part of this event
  const eventProductOptions = (event.EventProducts || []).map(ep => ({
    value: ep.productId,
    label: ep.Product?.name || `Product #${ep.productId}`,
  }));

  // Running inventory computation
  const runningInventory = (() => {
    const eps = event.EventProducts || [];
    const allDays = (event.EventDays || []).slice().sort((a, b) => a.date.localeCompare(b.date));
    const submittedDays = allDays.filter(d => d.submittedAt);
    const remainingDays = allDays.filter(d => !d.submittedAt);
    return eps.map(ep => {
      const pid = ep.productId;
      const totalRestock = allDays.reduce((s, d) => {
        const dp = (d.DayProducts || []).find(x => x.productId === pid);
        return s + (dp?.restockQty || 0);
      }, 0);
      const totalDisposed = allDays.reduce((s, d) => {
        return s + (d.Disposals || []).filter(x => x.productId === pid).reduce((a, x) => a + (x.qty || 0), 0);
      }, 0);
      // Find the most recent submitted day's endQty for this product
      let currentOnHand = null;
      for (let i = submittedDays.length - 1; i >= 0; i--) {
        const dp = (submittedDays[i].DayProducts || []).find(x => x.productId === pid);
        if (dp?.endQty != null) { currentOnHand = dp.endQty; break; }
      }
      // If no submitted day, estimate from starting inventory
      if (currentOnHand == null) currentOnHand = (ep.startingInventory || 0) + totalRestock - totalDisposed;
      const totalSold = (ep.startingInventory || 0) + totalRestock - totalDisposed - currentOnHand;
      const avgDailySales = submittedDays.length > 0 ? totalSold / submittedDays.length : null;
      const projectedNeed = avgDailySales != null ? Math.ceil(avgDailySales * remainingDays.length) : null;
      const suggestedOrder = projectedNeed != null ? Math.max(0, projectedNeed - currentOnHand) : null;
      return { ep, totalRestock, totalDisposed, currentOnHand, totalSold, avgDailySales, suggestedOrder, daysElapsed: submittedDays.length, daysRemaining: remainingDays.length };
    });
  })();

  const load = () => eventService.listSupplyOrders(event.id).then(setOrders).catch(() => setOrders([]));

  useEffect(() => { load(); }, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (idx, field, val) => setForm(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, [field]: val } : it),
  }));
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { productId: '', quantity: '' }] }));
  const epFor = (pid) => (event.EventProducts || []).find(e => String(e.productId) === String(pid));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleCreate = async () => {
    const qtyOf = (it) => (isWeight(epFor(it.productId)) ? parseFloat(it.quantity) : parseInt(it.quantity));
    const validItems = form.items.filter(it => it.productId && qtyOf(it) > 0);
    if (!validItems.length) { setFormError('Add at least one product with a quantity.'); return; }
    setSaving(true); setFormError('');
    try {
      await eventService.createSupplyOrder(event.id, {
        requestedDate: form.requestedDate || null,
        notes: form.notes.trim() || null,
        items: validItems.map(it => ({ productId: parseInt(it.productId), quantity: qtyOf(it) })),
      });
      setShowForm(false);
      setForm({ requestedDate: '', notes: '', items: [{ productId: '', quantity: '' }] });
      load();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to create order.');
      setSaving(false);
    }
  };

  const approvedLotCodes = (order) => (order.LotCodes || []).filter(lc => lc.status === 'approved');
  const pendingLotCodes  = (order) => (order.LotCodes || []).filter(lc => lc.status === 'pending_qa');

  if (!orders) return <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>;

  return (
    <div>
      {/* Running Inventory */}
      {runningInventory.length > 0 && (
        <div className="card mb-4">
          <div className="card-body py-2 px-3">
            <h6 className="fw-semibold mb-2" style={{ fontSize: 13 }}>Running Inventory</h6>
            <div className="table-responsive">
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead className="table-light">
                  <tr>
                    <th>Product</th>
                    <th className="text-end" title="Initial stock + all restocks so far">Total In</th>
                    <th className="text-end" title="Computed from inventory counts">Sold</th>
                    <th className="text-end">Disposed</th>
                    <th className="text-end" title="End of last submitted day, or estimate">On Hand</th>
                    <th className="text-end" title="Average daily sales across submitted days">Avg/Day</th>
                    <th className="text-end" title="Remaining event days">Days Left</th>
                    <th className="text-end" title="Projected need for remaining days minus on-hand">Suggested Order</th>
                  </tr>
                </thead>
                <tbody>
                  {runningInventory.map(({ ep, totalRestock, totalDisposed, currentOnHand, totalSold, avgDailySales, suggestedOrder, daysRemaining }) => (
                    <tr key={ep.productId}>
                      <td className="fw-semibold">{ep.Product?.name || `#${ep.productId}`}</td>
                      <td className="text-end">{fmtQty(ep, (ep.startingInventory || 0) + totalRestock)}</td>
                      <td className="text-end">{totalSold > 0 ? fmtQty(ep, totalSold) : '—'}</td>
                      <td className="text-end">{totalDisposed > 0 ? fmtQty(ep, totalDisposed) : '—'}</td>
                      <td className="text-end fw-semibold">
                        <span className={currentOnHand <= 0 ? 'text-danger' : currentOnHand < (avgDailySales || 0) ? 'text-warning' : ''}>
                          {fmtQty(ep, currentOnHand)}
                        </span>
                      </td>
                      <td className="text-end text-muted">{avgDailySales != null ? (isWeight(ep) ? fmtKg(avgDailySales) : avgDailySales.toFixed(1)) : '—'}</td>
                      <td className="text-end text-muted">{daysRemaining}</td>
                      <td className="text-end">
                        {suggestedOrder != null ? (
                          suggestedOrder > 0
                            ? <span className="badge bg-warning text-dark">{isWeight(ep) ? fmtKg(suggestedOrder) : `${suggestedOrder} units`}</span>
                            : <span className="text-success">✓ Sufficient</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {runningInventory[0]?.daysElapsed === 0 && (
              <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>Suggestions will appear once inventory has been submitted for at least one day.</p>
            )}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          {orders.length} supply order{orders.length !== 1 ? 's' : ''} — lot codes logged here feed the traceability record automatically on QA approval.
        </span>
        {!showForm && <button className="btn btn-sm btn-outline-primary" onClick={() => setShowForm(true)}>+ Request Supply</button>}
      </div>

      {showForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-3" style={{ fontSize: 13 }}>New Supply Order</h6>
            {formError && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{formError}</div>}
            <div className="row g-2 mb-3">
              <div className="col-5">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Requested Date</label>
                <input type="date" className="form-control form-control-sm" value={form.requestedDate} onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))} />
              </div>
              <div className="col-7">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Notes</label>
                <input type="text" className="form-control form-control-sm" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="optional" />
              </div>
            </div>
            {eventProductOptions.length === 0 && (
              <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 12 }}>
                No products added to this event yet. Add products in the Products tab first.
              </div>
            )}
            <div className="mb-2" style={{ fontSize: 12, fontWeight: 600 }}>Products</div>
            {form.items.map((item, idx) => (
              <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                <div style={{ flex: 2 }}>
                  <SearchableSelect
                    options={eventProductOptions}
                    value={item.productId}
                    onChange={val => setItem(idx, 'productId', val)}
                    placeholder="Select product…"
                    size="sm"
                  />
                </div>
                <input type="number" min={isWeight(epFor(item.productId)) ? '0.1' : '1'} step={isWeight(epFor(item.productId)) ? '0.1' : undefined} className="form-control form-control-sm" style={{ flex: 1 }} value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)} placeholder={isWeight(epFor(item.productId)) ? 'kg' : 'Qty'} title={isWeight(epFor(item.productId)) ? 'Quantity in kg' : undefined} />
                {form.items.length > 1 && <button className="btn btn-link btn-sm text-danger p-0" onClick={() => removeItem(idx)}>✕</button>}
              </div>
            ))}
            <button className="btn btn-link btn-sm p-0 mb-3" style={{ fontSize: 12 }} onClick={addItem}>+ Add product</button>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving}>{saving ? 'Saving…' : 'Create Order'}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setShowForm(false); setFormError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <p className="text-muted small">No supply orders yet. Create one to track lot codes for this event.</p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {orders.map(order => (
            <div key={order.id} className="card">
              <div className="card-body py-2 px-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="fw-semibold" style={{ fontSize: 13 }}>{order.orderNumber}</span>
                  <span className={`badge bg-${STATUS_COLOR[order.status] || 'secondary'} text-capitalize`} style={{ fontSize: 10 }}>{order.status?.replace(/_/g, ' ')}</span>
                </div>
                {order.requestedDate && <div className="text-muted" style={{ fontSize: 11 }}>Requested: {fmtDate(order.requestedDate)}</div>}

                {/* Items */}
                <div className="mt-2">
                  {(order.Items || []).map(item => (
                    <div key={item.id} className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                      <span>{item.Product?.name || `#${item.productId}`}</span>
                      <span className="text-muted">{item.quantity} units</span>
                    </div>
                  ))}
                </div>

                {/* Lot codes */}
                {(approvedLotCodes(order).length > 0 || pendingLotCodes(order).length > 0) && (
                  <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                    <div className="fw-semibold mb-1" style={{ fontSize: 11, color: '#6c757d' }}>LOT CODES</div>
                    {approvedLotCodes(order).map(lc => (
                      <span key={lc.id} className="badge bg-success me-1 mb-1" style={{ fontSize: 10 }}>
                        {lc.lotCode} ({lc.quantity} units) ✓
                      </span>
                    ))}
                    {pendingLotCodes(order).map(lc => (
                      <span key={lc.id} className="badge bg-warning text-dark me-1 mb-1" style={{ fontSize: 10 }}>
                        {lc.lotCode} ({lc.quantity} units) — pending QA
                      </span>
                    ))}
                  </div>
                )}

                {order.notes && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{order.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Personnel Tab ─────────────────────────────────────────────────────────────
function fmtHrsMin(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

const AVAIL_STYLE = {
  available:         { bg: '#d1e7dd', border: '#a3cfbb', color: '#0f5132' },
  available_partial: { bg: '#eaf5ee', border: '#a3cfbb', color: '#198754' },
  if_needed:         { bg: '#fff3cd', border: '#ffc107', color: '#664d03' },
  if_needed_partial: { bg: '#fffaec', border: '#ffc107', color: '#997a00' },
  unavailable:       { bg: '#f8d7da', border: '#f5c2c7', color: '#842029' },
};

function availCellStyle(a, day) {
  if (!a) return null;
  const shiftCount = day.shiftCount || 1;
  if (a.status === 'unavailable') return { ...AVAIL_STYLE.unavailable, symbol: '✗', shifts: null };
  const selectedShifts = a.availShifts ? a.availShifts.split(',').map(Number).filter(Boolean) : [];
  const isPartial = shiftCount > 1 && selectedShifts.length > 0 && selectedShifts.length < shiftCount;
  const shiftText = selectedShifts.length > 0 ? selectedShifts.join('·') : '✓';
  if (a.status === 'available') {
    return isPartial
      ? { ...AVAIL_STYLE.available_partial, symbol: shiftText, shifts: selectedShifts }
      : { ...AVAIL_STYLE.available, symbol: shiftText, shifts: selectedShifts };
  }
  // if_needed
  return isPartial
    ? { ...AVAIL_STYLE.if_needed_partial, symbol: shiftText, shifts: selectedShifts }
    : { ...AVAIL_STYLE.if_needed, symbol: shiftText, shifts: selectedShifts };
}

function AvailabilityGrid({ event, poolMembers }) {
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [editCell, setEditCell] = useState(null); // { employeeId, employeeName, dayId, day }
  const [editForm, setEditForm] = useState({ status: '', selectedShifts: new Set(), notes: '' });
  const [saving, setSaving] = useState(false);
  const [daySchedule, setDaySchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadGrid = useCallback(() => {
    if (!poolMembers.length) return;
    setLoading(true);
    eventService.getAvailabilityGrid(event.id)
      .then(setGrid)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [event.id, poolMembers]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  useEffect(() => {
    if (!editCell) { setDaySchedule(null); return; }
    setScheduleLoading(true);
    eventService.getDaySchedule(event.id, editCell.dayId)
      .then(setDaySchedule)
      .catch(() => setDaySchedule({ assignments: [], availabilities: [] }))
      .finally(() => setScheduleLoading(false));
  }, [editCell, event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (member, day, currentAvail) => {
    setTooltip(null);
    const selectedShifts = currentAvail?.availShifts
      ? new Set(currentAvail.availShifts.split(',').map(Number).filter(Boolean))
      : new Set();
    setEditForm({ status: currentAvail?.status || '', selectedShifts, notes: currentAvail?.notes || '' });
    setEditCell({
      employeeId: member.employeeId,
      employeeName: member.Employee ? `${member.Employee.firstName} ${member.Employee.lastName}` : `#${member.employeeId}`,
      dayId: day.id,
      day,
    });
  };

  const handleSave = async () => {
    if (!editCell) return;
    setSaving(true);
    try {
      const availShifts = editForm.selectedShifts.size > 0 ? Array.from(editForm.selectedShifts).sort().join(',') : null;
      await eventService.setAvailability(event.id, {
        employeeId: editCell.employeeId, eventDayId: editCell.dayId,
        status: editForm.status || null, notes: editForm.notes.trim() || null, availShifts,
      });
      setEditCell(null);
      loadGrid();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!editCell) return;
    setSaving(true);
    try {
      await eventService.setAvailability(event.id, { employeeId: editCell.employeeId, eventDayId: editCell.dayId, status: null });
      setEditCell(null);
      loadGrid();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const toggleShift = (n) => setEditForm(f => {
    const s = new Set(f.selectedShifts);
    s.has(n) ? s.delete(n) : s.add(n);
    return { ...f, selectedShifts: s };
  });

  const handleAssign = async (shiftNumber) => {
    if (!editCell) return;
    setAssigning(true);
    try {
      const existing = daySchedule?.assignments?.find(a => a.employeeId === editCell.employeeId);
      if (existing) {
        await eventService.updateAssignment(event.id, editCell.dayId, existing.id, { shiftNumber: shiftNumber || null });
      } else {
        await eventService.assignToDay(event.id, editCell.dayId, { employeeId: editCell.employeeId, shiftNumber: shiftNumber || null });
      }
      const data = await eventService.getDaySchedule(event.id, editCell.dayId);
      setDaySchedule(data);
    } catch { /* ignore */ }
    finally { setAssigning(false); }
  };

  const handleUnassign = async () => {
    if (!editCell) return;
    const existing = daySchedule?.assignments?.find(a => a.employeeId === editCell.employeeId);
    if (!existing) return;
    setAssigning(true);
    try {
      await eventService.removeAssignment(event.id, editCell.dayId, existing.id);
      const data = await eventService.getDaySchedule(event.id, editCell.dayId);
      setDaySchedule(data);
    } catch { /* ignore */ }
    finally { setAssigning(false); }
  };

  const handleExport = () => {
    if (!grid) return;
    const { days, availabilities } = grid;
    const aMap = {};
    availabilities.forEach(a => {
      if (!aMap[a.employeeId]) aMap[a.employeeId] = {};
      aMap[a.employeeId][a.eventDayId] = a;
    });

    const fmtCell = (a) => {
      if (!a) return '';
      let v = a.status === 'available' ? 'Available' : a.status === 'if_needed' ? 'If Needed' : 'Unavailable';
      if (a.availShifts && a.status !== 'unavailable') {
        v += ` (${a.availShifts.split(',').map(n => `S${n}`).join(', ')})`;
      }
      if (a.notes) v += ` – ${a.notes}`;
      return v;
    };

    const dayLabels = days.map(d => {
      const dt = new Date(d.date + 'T12:00:00');
      return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    });

    const header = ['Employee', 'Tags', ...dayLabels];
    const dataRows = poolMembers.map(m => {
      const name = m.Employee ? `${m.Employee.firstName} ${m.Employee.lastName}` : `#${m.employeeId}`;
      return [name, m.tags || '', ...days.map(d => fmtCell(aMap[m.employeeId]?.[d.id]))];
    });
    const availRow = ['— Available count', '', ...days.map(d =>
      availabilities.filter(a => a.eventDayId === d.id && a.status === 'available').length || ''
    )];
    const noReplyRow = ['— No response', '', ...days.map(d => {
      const replied = new Set(availabilities.filter(a => a.eventDayId === d.id).map(a => a.employeeId));
      const count = poolMembers.filter(m => !replied.has(m.employeeId)).length;
      return count || '';
    })];

    const csv = [header, ...dataRows, [], availRow, noReplyRow]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(event.name || 'event').replace(/[^a-z0-9]/gi, '_')}_availability.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm me-2" />Loading availability…</div>;
  if (!grid) return null;
  if (!poolMembers.length) return <p className="text-muted small">No employees in the pool yet.</p>;

  const { days, availabilities } = grid;
  if (!days.length) return <p className="text-muted small">No event days found.</p>;

  // Build lookup: availMap[employeeId][eventDayId] = { status, notes }
  const availMap = {};
  availabilities.forEach(a => {
    if (!availMap[a.employeeId]) availMap[a.employeeId] = {};
    availMap[a.employeeId][a.eventDayId] = a;
  });

  // Per-day counts
  const dayStats = days.map(d => {
    let avail = 0, unavail = 0, ifNeeded = 0, noReply = 0;
    poolMembers.forEach(m => {
      const a = availMap[m.employeeId]?.[d.id];
      if (!a) noReply++;
      else if (a.status === 'available') avail++;
      else if (a.status === 'unavailable') unavail++;
      else ifNeeded++;
    });
    return { avail, unavail, ifNeeded, noReply };
  });

  const fmtDay = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return {
      top: d.toLocaleDateString('en-US', { month: 'short' }),
      bottom: d.getDate(),
      dow: d.toLocaleDateString('en-US', { weekday: 'short' }),
    };
  };

  const colW = 38;

  return (
    <div style={{ position: 'relative' }}>
      {/* Legend */}
      <div className="d-flex gap-3 mb-2 flex-wrap" style={{ fontSize: 11 }}>
        {[
          { key: 'available',         label: 'Available (all shifts)',     symbol: '✓' },
          { key: 'available_partial', label: 'Available (some shifts)',    symbol: '1' },
          { key: 'if_needed',         label: 'If needed',                  symbol: '~' },
          { key: 'unavailable',       label: 'Unavailable',                symbol: '✗' },
        ].map(({ key, label, symbol }) => (
          <span key={key} className="d-flex align-items-center gap-1">
            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: AVAIL_STYLE[key].bg, border: `1px solid ${AVAIL_STYLE[key].border}`, textAlign: 'center', lineHeight: '13px', fontSize: 9, color: AVAIL_STYLE[key].color, fontWeight: 700 }}>{symbol}</span>
            <span className="text-muted">{label}</span>
          </span>
        ))}
        <span className="d-flex align-items-center gap-1">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#f8f9fa', border: '1px solid #dee2e6', textAlign: 'center', lineHeight: '13px', fontSize: 10, color: '#adb5bd' }}>·</span>
          <span className="text-muted">No response</span>
        </span>
        <span className="text-muted" style={{ fontSize: 10 }}>(Numbers in cells = shifts selected)</span>
        <span className="text-muted ms-auto" style={{ fontSize: 10 }}>Click any cell to set or edit availability</span>
        <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11, padding: '2px 10px' }} onClick={handleExport} disabled={!grid}>
          ↓ Export CSV
        </button>
      </div>

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 220 + days.length * colW }}>
          <thead>
            {/* Day header */}
            <tr>
              <th style={{ width: 180, minWidth: 140, position: 'sticky', left: 0, background: '#fff', zIndex: 2, fontSize: 11, color: '#6c757d', fontWeight: 600, paddingBottom: 4, borderBottom: '1px solid #dee2e6' }}>
                Employee
              </th>
              {days.map((d, di) => {
                const f = fmtDay(d.date);
                return (
                  <th key={d.id} style={{ width: colW, textAlign: 'center', fontSize: 10, color: '#6c757d', fontWeight: 600, verticalAlign: 'bottom', paddingBottom: 4, borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3 }}>{di === 0 || f.top !== fmtDay(days[di - 1].date).top ? f.top : ''}</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#212529' }}>{f.bottom}</div>
                    <div style={{ fontSize: 9, color: '#adb5bd' }}>{f.dow}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {poolMembers.map((m, mi) => {
              const name = m.Employee ? `${m.Employee.firstName} ${m.Employee.lastName}` : `#${m.employeeId}`;
              return (
                <tr key={m.id} style={{ background: mi % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ position: 'sticky', left: 0, background: mi % 2 === 0 ? '#fff' : '#f8f9fa', zIndex: 1, fontSize: 12, fontWeight: 500, paddingRight: 8, borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                    {name}
                    {m.tags && <span className="text-muted ms-1" style={{ fontSize: 10 }}>· {m.tags.split(',')[0].trim()}</span>}
                  </td>
                  {days.map(d => {
                    const a = availMap[m.employeeId]?.[d.id];
                    const s = availCellStyle(a, d);
                    const hasNote = a?.notes;
                    const tooltipText = [hasNote ? a.notes : null, s?.shifts?.length ? `Shifts: ${s.shifts.join(', ')}` : null].filter(Boolean).join(' · ');
                    return (
                      <td
                        key={d.id}
                        style={{ textAlign: 'center', padding: '3px 2px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                        onClick={() => openEdit(m, d, a)}
                        onMouseEnter={tooltipText ? (e) => setTooltip({ note: tooltipText, x: e.clientX, y: e.clientY }) : undefined}
                        onMouseLeave={tooltipText ? () => setTooltip(null) : undefined}
                      >
                        <span style={{
                          display: 'inline-block', width: 26, height: 26, borderRadius: 4,
                          background: s ? s.bg : '#f8f9fa',
                          border: `1px solid ${s ? s.border : '#dee2e6'}`,
                          color: s ? s.color : '#ced4da',
                          fontWeight: s ? 700 : 400,
                          fontSize: s ? 11 : 10,
                          lineHeight: '25px',
                          textAlign: 'center',
                          letterSpacing: -0.5,
                        }}>
                          {s ? s.symbol : '·'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Summary row */}
            <tr style={{ borderTop: '2px solid #dee2e6' }}>
              <td style={{ position: 'sticky', left: 0, background: '#fff', fontSize: 10, color: '#6c757d', fontWeight: 600, paddingTop: 4 }}>Available</td>
              {dayStats.map((s, di) => (
                <td key={di} style={{ textAlign: 'center', paddingTop: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: s.avail > 0 ? '#0f5132' : '#adb5bd' }}>{s.avail}</span>
                </td>
              ))}
            </tr>
            <tr>
              <td style={{ position: 'sticky', left: 0, background: '#fff', fontSize: 10, color: '#6c757d', fontWeight: 600 }}>No reply</td>
              {dayStats.map((s, di) => (
                <td key={di} style={{ textAlign: 'center' }}>
                  {s.noReply > 0 && <span style={{ fontSize: 11, color: '#adb5bd' }}>{s.noReply}</span>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 10, top: tooltip.y - 10, zIndex: 9999,
          background: '#212529', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 12, maxWidth: 220, pointerEvents: 'none',
        }}>
          {tooltip.note}
        </div>
      )}

      {editCell && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setEditCell(null)} />
          <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 360 }}>
              <div className="modal-content">
                <div className="modal-header py-2">
                  <div>
                    <h6 className="modal-title mb-0" style={{ fontSize: 13 }}>Set Availability</h6>
                    <div className="text-muted" style={{ fontSize: 11 }}>{editCell.employeeName} · {fmtDate(editCell.day.date)}</div>
                  </div>
                  <button className="btn-close btn-sm" onClick={() => setEditCell(null)} />
                </div>
                <div className="modal-body py-3">
                  <div className="mb-3">
                    <label className="form-label mb-2" style={{ fontSize: 11, fontWeight: 600 }}>Status</label>
                    <div className="d-flex gap-2">
                      {[
                        { value: 'available', label: 'Available', cls: 'btn-success' },
                        { value: 'if_needed', label: 'If Needed', cls: 'btn-warning' },
                        { value: 'unavailable', label: 'Unavailable', cls: 'btn-danger' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          className={`btn btn-sm ${editForm.status === opt.value ? opt.cls : 'btn-outline-secondary'}`}
                          style={{ fontSize: 12 }}
                          onClick={() => setEditForm(f => ({ ...f, status: f.status === opt.value ? '' : opt.value }))}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(editCell.day.shiftCount || 1) > 1 && ['available', 'if_needed'].includes(editForm.status) && (
                    <div className="mb-3">
                      <label className="form-label mb-2" style={{ fontSize: 11, fontWeight: 600 }}>
                        Shifts <span className="text-muted fw-normal">(blank = all shifts)</span>
                      </label>
                      <div className="d-flex gap-2 flex-wrap">
                        {Array.from({ length: editCell.day.shiftCount }, (_, i) => i + 1).map(n => {
                          const fullDay = (event.EventDays || []).find(d => d.id === editCell.dayId);
                          const start = fullDay?.[`shift${n}Start`];
                          const end = fullDay?.[`shift${n}End`];
                          const checked = editForm.selectedShifts.has(n);
                          return (
                            <button key={n} type="button"
                              className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                              style={{ fontSize: 11, padding: '3px 10px', minWidth: 44 }}
                              onClick={() => toggleShift(n)}
                            >
                              <div className="fw-semibold">S{n}</div>
                              {start && <div style={{ fontSize: 9 }}>{start.slice(0,5)}{end ? '–'+end.slice(0,5) : ''}</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="form-label mb-1" style={{ fontSize: 11, fontWeight: 600 }}>
                      Notes <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="e.g. can only stay until 6pm"
                      value={editForm.notes}
                      onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    />
                  </div>

                  {/* Schedule Assignment */}
                  <hr className="my-3" />
                  <div>
                    <label className="form-label mb-2 d-flex align-items-center gap-2" style={{ fontSize: 11, fontWeight: 600 }}>
                      Schedule Assignment
                      {scheduleLoading && <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2 }} />}
                    </label>
                    {(() => {
                      const existing = daySchedule?.assignments?.find(a => a.employeeId === editCell.employeeId);
                      const fullDay = (event.EventDays || []).find(d => d.id === editCell.dayId);
                      const shiftCount = editCell.day.shiftCount || 1;
                      const empUnavailable = editForm.status === 'unavailable';
                      const empShifts = editForm.selectedShifts.size > 0 ? editForm.selectedShifts : null;
                      return (
                        <>
                          {existing && (
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className="badge bg-primary" style={{ fontSize: 11 }}>
                                Assigned{existing.shiftNumber ? ` · S${existing.shiftNumber}` : ''}{existing.published ? ' ✓' : ' (draft)'}
                              </span>
                              <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 11 }} onClick={handleUnassign} disabled={assigning}>
                                Unassign
                              </button>
                            </div>
                          )}
                          <div className="d-flex gap-2 flex-wrap">
                            {Array.from({ length: shiftCount }, (_, i) => i + 1).map(n => {
                              const start = fullDay?.[`shift${n}Start`];
                              const end   = fullDay?.[`shift${n}End`];
                              const isAssigned = existing?.shiftNumber === n;
                              const availForShift = empUnavailable ? false : !empShifts ? null : empShifts.has(n);
                              let extraStyle = {};
                              let cls = 'btn-outline-secondary';
                              if (isAssigned) { cls = 'btn-primary'; }
                              else if (availForShift === true)  { extraStyle = { background: '#f0fff4', borderColor: '#a3cfbb', color: '#0f5132' }; }
                              else if (availForShift === false) { extraStyle = { background: '#fff5f5', borderColor: '#f5c2c7', color: '#842029' }; }
                              return (
                                <button key={n} type="button"
                                  className={`btn btn-sm ${cls}`}
                                  style={{ ...extraStyle, fontSize: 11, padding: '3px 10px', minWidth: 44 }}
                                  onClick={() => handleAssign(n)}
                                  disabled={assigning}
                                >
                                  <div className="fw-semibold">{isAssigned ? '✓ ' : ''}S{n}</div>
                                  {start && <div style={{ fontSize: 9 }}>{start.slice(0,5)}{end ? '–'+end.slice(0,5) : ''}</div>}
                                </button>
                              );
                            })}
                            <button type="button"
                              className={`btn btn-sm ${existing && !existing.shiftNumber ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              style={{ fontSize: 11, padding: '3px 10px' }}
                              onClick={() => handleAssign(null)}
                              disabled={assigning}
                              title="Assign without specifying a shift"
                            >—</button>
                          </div>
                          {!existing && !scheduleLoading && (
                            <div className="text-muted mt-1" style={{ fontSize: 10 }}>Click a shift to assign · — for unspecified</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="modal-footer py-2 d-flex justify-content-between">
                  <button className="btn btn-sm btn-outline-danger" onClick={handleClear} disabled={saving}>
                    Clear
                  </button>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditCell(null)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving || !editForm.status}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Shown once after creating / resetting an event-only staffer: the portal login to hand over.
function StaffCredentialsModal({ creds, name, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!creds) return null;
  const url = `${window.location.origin}/portal`;
  const text = `Portal: ${url}\nBadge number: ${creds.badgeNumber}\nTemporary password: ${creds.tempPassword}`;
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); } catch { /* ignore */ } };
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1080 }} />
      <div className="modal fade show d-block" style={{ zIndex: 1090 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
          <div className="modal-content">
            <div className="modal-header"><h6 className="modal-title mb-0">Portal login for {name}</h6></div>
            <div className="modal-body">
              <div className="alert alert-warning py-2" style={{ fontSize: 12 }}>This password is shown <strong>only once</strong>. Give it to them now — they'll be asked to choose their own on first sign-in.</div>
              <table className="table table-sm mb-2" style={{ fontSize: 14 }}>
                <tbody>
                  <tr><td className="text-muted">Sign in at</td><td><code>{url}</code></td></tr>
                  <tr><td className="text-muted">Badge number</td><td><code className="fs-6">{creds.badgeNumber}</code></td></tr>
                  <tr><td className="text-muted">Temporary password</td><td><code className="fs-6">{creds.tempPassword}</code></td></tr>
                </tbody>
              </table>
              <div className="text-muted" style={{ fontSize: 12 }}>They can then set their availability, see their schedule and clock in/out for this event.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
              <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PersonnelTab({ event, poolMembers, onPoolChange, isManager }) {
  const [allEmployees, setAllEmployees] = useState([]);
  // Event-only staff (not Pizzacini employees) creation + the one-time login shown afterwards
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [staffForm, setStaffForm] = useState({ firstName: '', lastName: '', hourlyRate: '', phone: '', email: '', breakMinutes: '0', tags: '' });
  const [staffSaving, setStaffSaving] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [credsView, setCredsView] = useState(null); // { creds, name }
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ employeeId: '', tags: '', isInventoryManager: false, hourlyRate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ tags: '', isInventoryManager: false, hourlyRate: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [personnelHours, setPersonnelHours] = useState({});
  const [personnelView, setPersonnelView] = useState('pool'); // 'pool' | 'availability'

  useEffect(() => {
    // A third-party manager must not see Pizzacini's employee list (and the API refuses it).
    if (isManager) return;
    axios.get(`${API_URL}/api/time-tracking/employees`, auth())
      .then(r => setAllEmployees(r.data.employees || r.data || []))
      .catch(() => {});
  }, [isManager]);

  useEffect(() => {
    eventService.getPersonnelHours(event.id)
      .then(hrs => {
        const map = {};
        (hrs || []).forEach(h => { map[h.employeeId] = h; });
        setPersonnelHours(map);
      })
      .catch(() => {});
  }, [event.id, poolMembers]);

  const inPool = new Set((poolMembers || []).map(m => m.employeeId));
  const availableEmployees = allEmployees.filter(e => !inPool.has(e.id) && e.status === 'active');

  const handleAdd = async () => {
    if (!form.employeeId) { setError('Select an employee.'); return; }
    setSaving(true); setError('');
    try {
      await eventService.addToPool(event.id, {
        employeeId: parseInt(form.employeeId),
        tags: form.tags.trim() || null,
        isInventoryManager: form.isInventoryManager,
        hourlyRate: form.hourlyRate !== '' ? parseFloat(form.hourlyRate) : null,
      });
      await onPoolChange();
      setShowAddForm(false);
      setForm({ employeeId: '', tags: '', isInventoryManager: false, hourlyRate: '' });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add.');
    } finally { setSaving(false); }
  };

  const openEdit = (m) => {
    setEditingMember(m);
    setEditForm({ tags: m.tags || '', isInventoryManager: m.isInventoryManager, hourlyRate: m.hourlyRate ?? '' });
  };

  const handleEdit = async () => {
    setEditSaving(true);
    try {
      await eventService.updatePoolMember(event.id, editingMember.id, {
        tags: editForm.tags.trim() || null,
        isInventoryManager: editForm.isInventoryManager,
        hourlyRate: editForm.hourlyRate !== '' ? parseFloat(editForm.hourlyRate) : null,
      });
      await onPoolChange();
      setEditingMember(null);
    } catch { /* ignore */ }
    finally { setEditSaving(false); }
  };

  const memberName = (m) => (m?.Employee ? `${m.Employee.firstName} ${m.Employee.lastName}` : `#${m?.employeeId}`);

  const handleCreateStaff = async () => {
    if (!staffForm.firstName.trim() || !staffForm.lastName.trim()) { setStaffError('First and last name are required.'); return; }
    setStaffSaving(true); setStaffError('');
    try {
      const res = await eventService.createStaff(event.id, {
        firstName: staffForm.firstName.trim(), lastName: staffForm.lastName.trim(),
        phone: staffForm.phone.trim() || null, email: staffForm.email.trim() || null,
        hourlyRate: staffForm.hourlyRate !== '' ? parseFloat(staffForm.hourlyRate) : null,
        breakMinutes: staffForm.breakMinutes !== '' ? parseInt(staffForm.breakMinutes, 10) : 0,
        tags: staffForm.tags.trim() || null,
      });
      await onPoolChange();
      setShowStaffForm(false);
      setStaffForm({ firstName: '', lastName: '', hourlyRate: '', phone: '', email: '', breakMinutes: '0', tags: '' });
      setCredsView({ creds: res.credentials, name: `${staffForm.firstName.trim()} ${staffForm.lastName.trim()}` });
    } catch (err) {
      setStaffError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to create staff member.');
    } finally { setStaffSaving(false); }
  };

  const handleResetLogin = async (m) => {
    try {
      const creds = await eventService.resetStaffPassword(event.id, m.employeeId);
      setCredsView({ creds, name: memberName(m) });
    } catch { /* ignore */ }
  };

  const handleRemove = async (m) => {
    try { await eventService.removeFromPool(event.id, m.id); await onPoolChange(); }
    catch { /* ignore */ }
    setConfirmRemove(null);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">{(poolMembers || []).length} employee{(poolMembers || []).length !== 1 ? 's' : ''} in this event pool</span>
          {(poolMembers || []).length > 0 && (
            <div className="btn-group btn-group-sm">
              <button className={`btn ${personnelView === 'pool' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setPersonnelView('pool')}>Pool</button>
              <button className={`btn ${personnelView === 'availability' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setPersonnelView('availability')}>Availability</button>
            </div>
          )}
        </div>
        {personnelView === 'pool' && !showAddForm && !showStaffForm && (
          <div className="d-flex gap-2">
            {!isManager && <button className="btn btn-sm btn-outline-primary" onClick={() => { setShowAddForm(true); setError(''); }}>+ Add to Pool</button>}
            <button className="btn btn-sm btn-outline-primary" onClick={() => { setShowStaffForm(true); setStaffError(''); }} title="Someone who isn't a Pizzacini employee — gets their own portal login for this event only">+ Add event staff</button>
          </div>
        )}
      </div>

      {showStaffForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-1" style={{ fontSize: 13 }}>Add event staff</h6>
            <p className="text-muted mb-3" style={{ fontSize: 11 }}>For people who work only this event and aren't Pizzacini employees. They won't appear in the employee list. A portal login (badge number + temporary password) is generated for them.</p>
            {staffError && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{staffError}</div>}
            <div className="row g-2 mb-2">
              <div className="col-6"><label className="form-label mb-1" style={{ fontSize: 12 }}>First name <span className="text-danger">*</span></label><input className="form-control form-control-sm" value={staffForm.firstName} onChange={e => setStaffForm(f => ({ ...f, firstName: e.target.value }))} /></div>
              <div className="col-6"><label className="form-label mb-1" style={{ fontSize: 12 }}>Last name <span className="text-danger">*</span></label><input className="form-control form-control-sm" value={staffForm.lastName} onChange={e => setStaffForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              <div className="col-6 col-md-3"><label className="form-label mb-1" style={{ fontSize: 12 }}>Hourly rate ($) <span className="text-muted" style={{ fontSize: 10 }}>for P&L</span></label><input type="number" min="0" step="0.01" className="form-control form-control-sm" value={staffForm.hourlyRate} onChange={e => setStaffForm(f => ({ ...f, hourlyRate: e.target.value }))} /></div>
              <div className="col-6 col-md-3"><label className="form-label mb-1" style={{ fontSize: 12 }}>Unpaid break (min)</label><input type="number" min="0" step="5" className="form-control form-control-sm" value={staffForm.breakMinutes} onChange={e => setStaffForm(f => ({ ...f, breakMinutes: e.target.value }))} /></div>
              <div className="col-6 col-md-3"><label className="form-label mb-1" style={{ fontSize: 12 }}>Phone <span className="text-muted" style={{ fontSize: 10 }}>optional</span></label><input className="form-control form-control-sm" value={staffForm.phone} onChange={e => setStaffForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="col-6 col-md-3"><label className="form-label mb-1" style={{ fontSize: 12 }}>Tags <span className="text-muted" style={{ fontSize: 10 }}>optional</span></label><input className="form-control form-control-sm" value={staffForm.tags} onChange={e => setStaffForm(f => ({ ...f, tags: e.target.value }))} placeholder="cashier, runner" /></div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleCreateStaff} disabled={staffSaving}>{staffSaving ? 'Creating…' : 'Create & generate login'}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowStaffForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <StaffCredentialsModal creds={credsView?.creds} name={credsView?.name} onClose={() => setCredsView(null)} />

      {showAddForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-3" style={{ fontSize: 13 }}>Add Employee to Pool</h6>
            {error && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
            <div className="mb-2">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Employee <span className="text-danger">*</span></label>
              <SearchableSelect
                options={availableEmployees.map(e => ({ value: e.id, label: `${e.firstName} ${e.lastName}${e.badgeNumber ? ` (${e.badgeNumber})` : ''}` }))}
                value={form.employeeId}
                onChange={val => setForm(f => ({ ...f, employeeId: val }))}
                placeholder="Select employee…"
                size="sm"
              />
            </div>
            <div className="row g-2 mb-2">
              <div className="col-6">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Tags <span className="text-muted" style={{ fontSize: 10 }}>comma-separated</span></label>
                <input type="text" className="form-control form-control-sm" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. cashier, runner" />
              </div>
              <div className="col-6">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Hourly Rate ($) <span className="text-muted" style={{ fontSize: 10 }}>for P&L</span></label>
                <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.hourlyRate} onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))} placeholder="e.g. 17.00" />
              </div>
            </div>
            <div className="form-check mb-3">
              <input type="checkbox" className="form-check-input" id="isInvMgr" checked={form.isInventoryManager} onChange={e => setForm(f => ({ ...f, isInventoryManager: e.target.checked }))} />
              <label className="form-check-label" htmlFor="isInvMgr" style={{ fontSize: 12 }}>Inventory Manager (can submit daily counts via portal)</label>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>{saving ? 'Adding…' : 'Add'}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editingMember && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setEditingMember(null)} />
          <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
              <div className="modal-content">
                <div className="modal-header py-2">
                  <h6 className="modal-title mb-0">Edit Pool Member</h6>
                  <button className="btn-close" onClick={() => setEditingMember(null)} />
                </div>
                <div className="modal-body">
                  <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                    {editingMember.Employee ? `${editingMember.Employee.firstName} ${editingMember.Employee.lastName}` : `#${editingMember.employeeId}`}
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Tags</label>
                    <input type="text" className="form-control form-control-sm" value={editForm.tags} onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))} placeholder="comma-separated" />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Hourly Rate ($)</label>
                    <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={editForm.hourlyRate} onChange={e => setEditForm(f => ({ ...f, hourlyRate: e.target.value }))} />
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="editInvMgr" checked={editForm.isInventoryManager} onChange={e => setEditForm(f => ({ ...f, isInventoryManager: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="editInvMgr" style={{ fontSize: 12 }}>Inventory Manager</label>
                  </div>
                </div>
                <div className="modal-footer py-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingMember(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {personnelView === 'availability' ? (
        <AvailabilityGrid event={event} poolMembers={poolMembers || []} />
      ) : (poolMembers || []).length === 0 ? (
        <p className="text-muted small">No employees in the pool yet. Add team members who will work this event.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm" style={{ fontSize: 13 }}>
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Tags</th>
                <th className="text-center">Inv. Mgr</th>
                <th className="text-end">Hourly Rate</th>
                <th className="text-end">Days</th>
                <th className="text-end">Sched. Hours</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {poolMembers.map(m => {
                const hrs = personnelHours[m.employeeId];
                return (
                <tr key={m.id}>
                  <td>
                    {memberName(m)}
                    {m.Employee?.staffType === 'event_staff' && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }} title="Not a Pizzacini employee — event-only portal login">Event staff</span>}
                  </td>
                  <td>
                    {m.tags ? m.tags.split(',').map(t => (
                      <span key={t} className="badge bg-light text-dark me-1 border" style={{ fontSize: 10 }}>{t.trim()}</span>
                    )) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-center">
                    {m.isInventoryManager ? <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>Yes</span> : '—'}
                  </td>
                  <td className="text-end">{m.hourlyRate != null ? fmt$(m.hourlyRate) : '—'}</td>
                  <td className="text-end text-muted" style={{ fontSize: 12 }}>{hrs ? hrs.totalDays : '—'}</td>
                  <td className="text-end text-muted" style={{ fontSize: 12 }}>{hrs ? fmtHrsMin(hrs.totalMinutes) : '—'}</td>
                  <td className="text-end">
                    <button className="btn btn-link btn-sm p-0 me-2" onClick={() => openEdit(m)}>Edit</button>
                    {m.Employee?.staffType === 'event_staff' && (
                      <button className="btn btn-link btn-sm p-0 me-2" onClick={() => handleResetLogin(m)} title="Generate a new temporary portal password">Reset login</button>
                    )}
                    <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setConfirmRemove(m)}>Remove</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        show={!!confirmRemove}
        title="Remove from pool"
        message={`Remove ${confirmRemove?.Employee ? `${confirmRemove.Employee.firstName} ${confirmRemove.Employee.lastName}` : 'this employee'} from the event pool?${confirmRemove?.Employee?.staffType === 'event_staff' ? ' This also deactivates their portal login.' : ''}`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => handleRemove(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = usePermissions();
  // Third-party event manager: may run personnel/schedule/inventory but not change the event's
  // terms or catalog products — those controls render locked.
  const isManager = role === 'event_manager';
  const isAdminRole = role === 'admin' || role === 'superadmin';
  const [showManagersModal, setShowManagersModal] = useState(false);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('calendar');
  const [poolMembers, setPoolMembers] = useState([]);

  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const loadPool = useCallback(async (eid) => {
    try { setPoolMembers(await eventService.listPool(eid)); }
    catch { setPoolMembers([]); }
  }, []);

  useEffect(() => {
    eventService.getEvent(id)
      .then(ev => { setEvent(ev); loadPool(ev.id); })
      .catch(() => setError('Event not found.'))
      .finally(() => setLoading(false));
  }, [id, loadPool]);

  const handleDayClick = (day) => { setSelectedDay(day); setShowDayModal(true); };

  const handleLockToggle = async (day) => {
    try {
      await eventService.setDayLock(event.id, day.id, !day.isLocked);
      const updated = await eventService.getEvent(event.id);
      setEvent(updated);
      const updatedDay = updated.EventDays?.find(d => d.id === day.id);
      if (updatedDay) setSelectedDay(updatedDay);
    } catch { /* ignore */ }
  };

  const handleEventUpdate = (updated) => { setEvent(updated); };

  if (loading) return <div className="container mt-5 text-center"><span className="spinner-border" /></div>;
  if (error || !event) return <div className="container mt-4"><div className="alert alert-danger">{error || 'Event not found.'}</div></div>;

  const totalDays = (event.EventDays || []).length;
  const submittedDays = (event.EventDays || []).filter(d => d.submittedAt).length;

  return (
    <div className="container-xxl mt-4 mb-5">
      {/* Header */}
      <div className="d-flex align-items-start gap-3 mb-3">
        <button className="btn btn-link p-0 text-muted" onClick={() => navigate('/events')} style={{ fontSize: 13 }}>← Events</button>
      </div>
      <div className="d-flex align-items-center gap-3 mb-1">
        <h4 className="mb-0">{event.name}</h4>
        <span className={`badge bg-${STATUS_BADGE[event.status] || 'secondary'} text-capitalize`}>{event.status}</span>
      </div>
      <div className="text-muted small mb-3">
        {fmtDate(event.startDate)} → {fmtDate(event.endDate)} &nbsp;·&nbsp; {totalDays} days &nbsp;·&nbsp; {submittedDays}/{totalDays} submitted
        {event.entryFee && <> &nbsp;·&nbsp; Entry fee: {fmt$(event.entryFee)}</>}
        {event.concessionsCommissionPct && <> &nbsp;·&nbsp; Commission: {event.concessionsCommissionPct}%</>}
        {isManager ? (
          <LockedAction className="btn btn-link btn-sm p-0 ms-2" style={{ fontSize: 12 }} what="change the event's entry fee, commission, location, dates or status">Edit</LockedAction>
        ) : (
          <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => setShowEditModal(true)} style={{ fontSize: 12 }}>Edit</button>
        )}
        {isAdminRole && (
          <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => setShowManagersModal(true)} style={{ fontSize: 12 }} title="Third-party users who can manage this event">Managers</button>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {['calendar', 'products', 'supply', 'personnel'].map(tab => (
          <li key={tab} className="nav-item">
            <button className={`nav-link text-capitalize ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab}
              {tab === 'personnel' && poolMembers.length > 0 && (
                <span className="badge bg-light text-dark ms-1 border" style={{ fontSize: 10 }}>{poolMembers.length}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {activeTab === 'calendar' && (
        <>
          <div className="d-flex justify-content-end mb-2">
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }} onClick={() => setShowImportModal(true)}>
              📄 Import hours from PDF
            </button>
          </div>
          <CalendarTab event={event} onDayClick={handleDayClick} />
        </>
      )}
      {activeTab === 'products' && (
        <ProductsTab event={event} onEventUpdate={handleEventUpdate} readOnly={isManager} />
      )}
      {activeTab === 'supply' && (
        <SupplyTab event={event} />
      )}
      {activeTab === 'personnel' && (
        <PersonnelTab event={event} poolMembers={poolMembers} onPoolChange={() => loadPool(event.id)} isManager={isManager} />
      )}

      {/* Schedule PDF import modal */}
      <ScheduleImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        event={event}
        onApplied={() => {
          eventService.getEvent(event.id).then(setEvent).catch(() => {});
        }}
      />

      {/* Day detail modal */}
      <DayDetailModal
        show={showDayModal}
        onClose={() => setShowDayModal(false)}
        day={selectedDay}
        days={(event.EventDays || []).slice().sort((a, b) => a.date.localeCompare(b.date))}
        onNavigate={setSelectedDay}
        eventId={event.id}
        eventProducts={event.EventProducts || []}
        poolMembers={poolMembers}
        onLockToggle={handleLockToggle}
        isManager={isManager}
        onDayUpdate={(updatedDay) => {
          setSelectedDay(updatedDay);
          setEvent(ev => ({
            ...ev,
            EventDays: (ev.EventDays || []).map(d => d.id === updatedDay.id ? { ...d, ...updatedDay } : d),
          }));
        }}
        onDayDelete={(dayId) => {
          setShowDayModal(false);
          setEvent(ev => ({
            ...ev,
            EventDays: (ev.EventDays || []).filter(d => d.id !== dayId),
          }));
        }}
      />

      {showManagersModal && (
        <EventManagersModal eventId={event.id} onClose={() => setShowManagersModal(false)} />
      )}

      {/* Edit event modal — reuse same form from EventsPage */}
      {showEditModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setShowEditModal(false)} />
          <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Event</h5>
                  <button className="btn-close" onClick={() => setShowEditModal(false)} />
                </div>
                <EditEventForm
                  event={event}
                  onSaved={(updated) => { setEvent(updated); setShowEditModal(false); }}
                  onClose={() => setShowEditModal(false)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Admin-only: choose which third-party (event_manager) users can manage this event.
function EventManagersModal({ eventId, onClose }) {
  const [assigned, setAssigned] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mgrs, users] = await Promise.all([
        eventService.listManagers(eventId),
        axios.get(`${API_URL}/api/users`, auth()).then(r => r.data),
      ]);
      setAssigned(mgrs);
      const assignedIds = new Set(mgrs.map(m => m.id));
      setCandidates((users || []).filter(u => u.role === 'event_manager' && u.isActive !== false && !assignedIds.has(u.id)));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load managers.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const label = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username;

  const add = async () => {
    if (!selected) return;
    setError('');
    try { await eventService.addManager(eventId, Number(selected)); setSelected(''); await load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to assign.'); }
  };
  const remove = async (u) => {
    try { await eventService.removeManager(eventId, u.id); await load(); } catch { /* ignore */ }
    setConfirmRemove(null);
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontSize: 16 }}>Event managers</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small">Users with the <strong>Event Manager</strong> role assigned here can manage this event only — personnel, schedule, inventory and P/L — but cannot change its entry fee, location or products. Create the user under Admin → User Management.</p>
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 12 }}>{error}</div>}
              {loading ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div> : (
                <>
                  {assigned.length === 0
                    ? <p className="text-muted small fst-italic">No managers assigned yet.</p>
                    : (
                      <ul className="list-group mb-3">
                        {assigned.map(u => (
                          <li key={u.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                            <span>{label(u)} <span className="text-muted small">@{u.username}</span></span>
                            <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setConfirmRemove(u)}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm" value={selected} onChange={e => setSelected(e.target.value)}>
                      <option value="">{candidates.length ? 'Select an Event Manager user…' : 'No unassigned Event Manager users'}</option>
                      {candidates.map(u => <option key={u.id} value={u.id}>{label(u)} (@{u.username})</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={add} disabled={!selected}>Assign</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        show={!!confirmRemove}
        title="Remove manager"
        message={`Remove ${confirmRemove ? label(confirmRemove) : ''} from this event? They will lose access to it immediately.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => remove(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />
    </>
  );
}

// Inline edit form used inside the modal above
function EditEventForm({ event, onSaved, onClose }) {
  const [form, setForm] = useState({
    name: event.name || '',
    startDate: event.startDate || '',
    endDate: event.endDate || '',
    entryFee: event.entryFee ?? '',
    concessionsCommissionPct: event.concessionsCommissionPct ?? '',
    entranceCommissionPerUnit: event.entranceCommissionPerUnit ?? '',
    status: event.status || 'draft',
    notes: event.notes || '',
    venueAddress: event.venueAddress || '',
    venueLat: event.venueLat != null ? String(event.venueLat) : '',
    venueLng: event.venueLng != null ? String(event.venueLng) : '',
    geoRadius: event.geoRadius != null ? String(event.geoRadius) : '300',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pinning, setPinning] = useState(false);
  const [pinError, setPinError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pinMyLocation = () => {
    if (!navigator.geolocation) { setPinError('Geolocation not supported by this browser.'); return; }
    setPinning(true); setPinError('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({ ...f, venueLat: String(pos.coords.latitude.toFixed(7)), venueLng: String(pos.coords.longitude.toFixed(7)) }));
        setPinning(false);
      },
      () => { setPinError('Could not get location. Check browser permissions.'); setPinning(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) { setError('Name, start date and end date are required.'); return; }
    setSaving(true); setError('');
    try {
      const updated = await eventService.updateEvent(event.id, {
        name: form.name.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        entryFee: form.entryFee !== '' ? parseFloat(form.entryFee) : null,
        concessionsCommissionPct: form.concessionsCommissionPct !== '' ? parseFloat(form.concessionsCommissionPct) : null,
        entranceCommissionPerUnit: form.entranceCommissionPerUnit !== '' ? parseFloat(form.entranceCommissionPerUnit) : null,
        status: form.status,
        notes: form.notes.trim() || null,
        venueAddress: form.venueAddress.trim() || null,
        venueLat: form.venueLat !== '' ? parseFloat(form.venueLat) : null,
        venueLng: form.venueLng !== '' ? parseFloat(form.venueLng) : null,
        geoRadius: form.geoRadius !== '' ? parseInt(form.geoRadius) : 300,
      });
      const fresh = await eventService.getEvent(updated.id);
      onSaved(fresh);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save.'); setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {error && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 13 }}>{error}</div>}
        <div className="mb-3">
          <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Event Name</label>
          <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="row g-2 mb-3">
          <div className="col">
            <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Start Date</label>
            <input type="date" className="form-control form-control-sm" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>
          <div className="col">
            <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>End Date</label>
            <input type="date" className="form-control form-control-sm" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>
        <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
          <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Financials</div>
          <div className="row g-2">
            <div className="col-12">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Entry Fee ($)</label>
              <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.entryFee} onChange={e => set('entryFee', e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Concessions commission (%)</label>
              <input type="number" min="0" max="100" step="0.1" className="form-control form-control-sm" value={form.concessionsCommissionPct} onChange={e => set('concessionsCommissionPct', e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Entrance commission ($/unit)</label>
              <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.entranceCommissionPerUnit} onChange={e => set('entranceCommissionPerUnit', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Status</label>
          <select className="form-select form-select-sm" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="mb-3 p-2 rounded" style={{ background: '#f0f7ff', border: '1px solid #cfe2ff' }}>
          <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Venue &amp; Geolocation</div>
          <div className="text-muted mb-2" style={{ fontSize: 11 }}>
            Set the event location so employees can clock in from their phones. They must be within the radius to punch.
          </div>
          <div className="mb-2">
            <label className="form-label mb-1" style={{ fontSize: 12 }}>Venue Address</label>
            <input type="text" className="form-control form-control-sm" value={form.venueAddress} onChange={e => set('venueAddress', e.target.value)} placeholder="e.g. 1 Stadium Dr, Miami, FL" />
          </div>
          <div className="row g-2 mb-2">
            <div className="col-5">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Latitude</label>
              <input type="number" step="any" className="form-control form-control-sm" value={form.venueLat} onChange={e => set('venueLat', e.target.value)} placeholder="e.g. 25.7617" />
            </div>
            <div className="col-5">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Longitude</label>
              <input type="number" step="any" className="form-control form-control-sm" value={form.venueLng} onChange={e => set('venueLng', e.target.value)} placeholder="e.g. -80.1918" />
            </div>
            <div className="col-2 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary w-100"
                style={{ fontSize: 11 }}
                onClick={pinMyLocation}
                disabled={pinning}
                title="Capture your current GPS coordinates"
              >
                {pinning ? '…' : 'Pin'}
              </button>
            </div>
          </div>
          {pinError && <div className="text-danger mb-1" style={{ fontSize: 11 }}>{pinError}</div>}
          {form.venueLat && form.venueLng && (
            <div className="text-success mb-2" style={{ fontSize: 11 }}>
              ✓ Location set: {parseFloat(form.venueLat).toFixed(5)}, {parseFloat(form.venueLng).toFixed(5)}
            </div>
          )}
          <div>
            <label className="form-label mb-1" style={{ fontSize: 12 }}>
              Geo-fence Radius: <strong>{form.geoRadius} m</strong>
              <span className="text-muted fw-normal ms-1">(employees must be within this distance)</span>
            </label>
            <input
              type="range" className="form-range" min="50" max="1000" step="25"
              value={form.geoRadius}
              onChange={e => set('geoRadius', e.target.value)}
            />
            <div className="d-flex justify-content-between text-muted" style={{ fontSize: 10 }}>
              <span>50 m (tight)</span><span>300 m (default)</span><span>1 km (loose)</span>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Notes</label>
          <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
      </div>
    </>
  );
}
